import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Grid,
  LinearProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  TextField,
  IconButton,
  Avatar,
  InputBase,
  Badge,
  alpha,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  AttachMoney as AttachMoneyIcon,
  Launch as LaunchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  CalendarToday as CalendarTodayIcon,
  Close as CloseIcon,
  CheckCircleOutlineOutlined as CheckCircleOutlineIcon,
  Circle as CircleIcon,
  ChatBubble as ChatBubbleIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useParams, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { getProject, updateProject, updateTaskStatus, approveTask, advancePhase, setProjectPhase, generateRaciTasks, getProjectChannel, getChannels, sendMessage, markRead, addChannelMembers, getMessagingUsers, getProjectProposal, generateInstallationTasks, getInstallationTasks, updateInstallationTask, bulkAssignTasks, getUsers, assignProject, assignRaciTask, getProjectMeetings, createWorkItem, getUnbillableLabor, addUnbillableLabor, deleteUnbillableLabor, getProjectDocuments, uploadProjectDocument, deleteProjectDocument, regenerateProposal, getProposalRegenerationHistory, getProjectAliases, addProjectAlias, deleteProjectAlias } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { format as dateFnsFormat, isToday, isYesterday } from 'date-fns';
import VoiceNote from '../components/VoiceNote';
import ProjectContacts from '../components/ProjectContacts';
import FieldNotes from '../components/FieldNotes';
import WorkItems from '../components/WorkItems';
import RackBuilder from '../components/RackBuilder';
import { Mic as MicIcon, FilterList as FilterListIcon, Build as BuildIcon, Warning as WarningIcon, MoneyOff as MoneyOffIcon, Delete as DeleteIcon, Add as AddIcon, Description as DescriptionIcon, CloudUpload as CloudUploadIcon, PictureAsPdf as PdfIcon, OpenInNew as OpenInNewIcon, Storage as StorageIcon } from '@mui/icons-material';
import { FormControl, Select, MenuItem, InputLabel, Collapse, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { TransitionGroup } from 'react-transition-group';

// Department color mapping for notes
const NOTE_DEPT_COLORS = {
  'PM': { color: '#2E7D32', label: 'PM Notes' },
  'Field Operations Manager': { color: '#1565C0', label: 'Tech Notes' },
  'Installation Technician': { color: '#1565C0', label: 'Tech Notes' },
  'Lead Tech': { color: '#1565C0', label: 'Tech Notes' },
  'AfterCare Admin Manager': { color: '#FF8F00', label: 'AfterCare Notes' },
  'AfterCare Tech Manager': { color: '#FF8F00', label: 'AfterCare Notes' },
  'AfterCare Technician': { color: '#FF8F00', label: 'AfterCare Notes' },
  'Service Tech': { color: '#FF8F00', label: 'AfterCare Notes' },
  'AfterCare': { color: '#FF8F00', label: 'AfterCare Notes' },
  'Lighting Manager': { color: '#7B1FA2', label: 'Lighting & Shades' },
  'Shade Field Manager': { color: '#7B1FA2', label: 'Lighting & Shades' },
  'Shade Design & Sales Manager': { color: '#7B1FA2', label: 'Lighting & Shades' },
  'Lighting Tech': { color: '#7B1FA2', label: 'Lighting & Shades' },
  'Shade Tech': { color: '#7B1FA2', label: 'Lighting & Shades' },
  'Lighting & Shading Tech': { color: '#7B1FA2', label: 'Lighting & Shades' },
  'IT': { color: '#00838F', label: 'IT' },
  'Programming': { color: '#283593', label: 'Programming' },
  'Engineering': { color: '#4E342E', label: 'Engineering' },
  'CAD Manager': { color: '#4E342E', label: 'Engineering' },
  'CAD Operator': { color: '#4E342E', label: 'Engineering' },
  'Management': { color: '#37474F', label: 'Management' },
  'Sales': { color: '#E65100', label: 'Sales' },
};

const getNoteColor = (role) => NOTE_DEPT_COLORS[role]?.color || '#616161';
const getNoteCategory = (role) => {
  const mapping = NOTE_DEPT_COLORS[role];
  if (!mapping) return 'Other';
  return mapping.label;
};

function fmtCurrency(val) {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtDate(d) {
  if (!d) return '—';
  // Defensive: never return a non-string — React would throw error #31 if a
  // bare object got rendered as a child. The auth-redaction middleware used
  // to leak `{}` for Date fields; this is the last line of defence.
  if (typeof d === 'object' && !(d instanceof Date)) return '—';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '—';
    return format(dt, 'MMM d, yyyy');
  } catch {
    return typeof d === 'string' ? d : '—';
  }
}

const PHASE_NAMES = [
  'Proposal',
  'Engineering & Kick-off',
  'Rough-in',
  'Pre-Final',
  'Final',
  'Final Billing',
  'AfterCare',
];

// ─── OptiPortal Deep Links ────────────────────────────────────────────────────

const OPTI_BASE = 'https://optiportal.audiocommand.com/Report-Viewer';

const OPTI_REPORTS = [
  { label: 'JSRDTL',       key: 'JSR_DTwLoc', color: '#2e7d32', extra: '',                    title: 'D-Tools Job Status Report with Location' },
  { label: 'Dash',         key: 'PMDASH',     color: '#1565c0', extra: '',                    title: 'PM Dashboard' },
  { label: 'Proposal',     key: 'PROPOSAL',   color: '#e65100', extra: '&_CRShowPrices=True', title: 'D-Tools Proposal' },
  { label: 'Labor Hours',  key: 'LH',         color: '#00695c', extra: '',                    title: 'Labor Hours' },
  { label: 'AR',           key: 'AR',         color: '#c62828', extra: '',                    title: 'Accounts Receivable' },
  { label: 'Change Orders',key: 'CO',         color: '#6a1b9a', extra: '',                    title: 'Change Orders' },
  { label: 'Bin',          key: 'BIN',        color: '#4e342e', extra: '',                    title: 'Inventory / Bin' },
  { label: 'P&L',          key: 'PNP',        color: '#546e7a', extra: '',                    title: 'Pick-N-Pack / P&L' },
];

function optiUrl(reportKey, jobNumber, extra = '') {
  return `${OPTI_BASE}?key=${reportKey}&_CRJob=${encodeURIComponent(jobNumber)}&renderas=pdf${extra}`;
}

// Deals in these states never represent a real, reportable job in OptiPortal:
//   - Closed Lost (CRM)                            → lost deal, no work
//   - ARCHIVE - Lost / ARCHIVE - Alternate (D-Tools) → archived/superseded
//   - Rejected (D-Tools)                            → proposal rejected
// A job_number is included ONLY if it's the project's primary_job_number OR if
// at least one deal with that job_number is in a live state. This prevents
// dead/archived deals from leaking extra OptiPortal report rows.
const DEAD_DTOOLS_PROGRESS = new Set([
  'ARCHIVE - Lost',
  'ARCHIVE - Alternate',
  'Rejected',
]);

const isDeadDeal = (deal) => {
  if (!deal) return true;
  if (deal.crm_stage === 'Closed Lost') return true;
  if (DEAD_DTOOLS_PROGRESS.has(deal.d_tools_progress)) return true;
  return false;
};

const OptiPortalReports = ({ project }) => {
  const [showRelated, setShowRelated] = React.useState(false);

  // Primary job = the one this Command Center record represents. That's the
  // ONLY row shown by default. Other deal job_numbers are CRM-linked siblings
  // (often other trades at the same address, e.g. AV/IT vs Lighting vs Shades)
  // and live behind "Show related jobs" so a project page never silently
  // explodes into half-a-dozen OptiPortal rows again.
  const primaryJob = project.primary_job_number || null;

  // Build the list of related job numbers: any LIVE deal job_number that isn't
  // the primary. Dead deals (Closed Lost / ARCHIVE / Rejected) are dropped
  // entirely — they should never produce OptiPortal report buttons.
  const relatedJobs = [];
  if (project.deals?.length) {
    const dealsByJob = new Map();
    project.deals.forEach((deal) => {
      if (!deal.job_number) return;
      if (deal.job_number === primaryJob) return;
      if (!dealsByJob.has(deal.job_number)) dealsByJob.set(deal.job_number, []);
      dealsByJob.get(deal.job_number).push(deal);
    });
    dealsByJob.forEach((deals, jobNum) => {
      if (deals.some((d) => !isDeadDeal(d))) {
        relatedJobs.push(jobNum);
      }
    });
  }

  // Nothing to show at all? Bail.
  if (!primaryJob && relatedJobs.length === 0) return null;

  const visibleJobs = [];
  if (primaryJob) visibleJobs.push(primaryJob);
  if (showRelated) visibleJobs.push(...relatedJobs);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          OptiPortal Reports
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LaunchIcon sx={{ fontSize: 13 }} />
          Reports open in OptiPortal (login required)
        </Typography>
      </Box>

      {visibleJobs.map((jobNum) => {
        const isPrimary = jobNum === primaryJob;
        return (
          <Box key={jobNum} sx={{ mb: visibleJobs.length > 1 ? 3 : 0 }}>
            {visibleJobs.length > 1 && (
              <Typography
                variant="body2"
                fontWeight="bold"
                color={isPrimary ? 'primary.main' : 'text.secondary'}
                sx={{ mb: 1 }}
              >
                Job #{jobNum}{!isPrimary && ' (related)'}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {OPTI_REPORTS.map((report) => (
                <Button
                  key={report.key}
                  component="a"
                  href={optiUrl(report.key, jobNum, report.extra)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="small"
                  endIcon={<LaunchIcon sx={{ fontSize: '0.8rem !important' }} />}
                  title={report.title}
                  sx={{
                    bgcolor: report.color,
                    '&:hover': { bgcolor: report.color, filter: 'brightness(0.85)' },
                    textTransform: 'none',
                    fontWeight: report.key === 'JSR_DTwLoc' ? 'bold' : 'normal',
                    fontSize: report.key === 'JSR_DTwLoc' ? '0.85rem' : '0.75rem',
                    px: report.key === 'JSR_DTwLoc' ? 2 : 1.5,
                  }}
                >
                  {report.label}
                </Button>
              ))}
            </Box>
          </Box>
        );
      })}

      {relatedJobs.length > 0 && (
        <Button
          size="small"
          onClick={() => setShowRelated((v) => !v)}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          {showRelated
            ? 'Hide related jobs'
            : `Show ${relatedJobs.length} related job${relatedJobs.length > 1 ? 's' : ''} (${relatedJobs.join(', ')})`}
        </Button>
      )}
    </Paper>
  );
};

// RACI type label and color helpers
const RACI_COLORS = { R: '#1565C0', A: '#2E7D32', C: '#E65100', I: '#6A1B9A' };
const RACI_LABELS = { R: 'Responsible', A: 'Accountable', C: 'Consulted', I: 'Informed' };

const RaciChip = ({ type }) => (
  <Chip
    label={type}
    size="small"
    sx={{
      fontSize: '0.6rem',
      height: 16,
      fontWeight: 'bold',
      bgcolor: `${RACI_COLORS[type]}18`,
      color: RACI_COLORS[type],
      border: `1px solid ${RACI_COLORS[type]}40`,
      minWidth: 22,
      '& .MuiChip-label': { px: 0.5 },
    }}
  />
);

const TaskItem = ({ task, onStatusChange, onApprove, currentUser, projectPmId, isPmOrMgmt, onAssign, allUsers }) => {
  const [loading, setLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const isResponsible = task.assignments?.some(
    (a) => a.raci_type === 'R' && a.user_id === currentUser.id
  );
  const isAccountable = task.assignments?.some(
    (a) => a.raci_type === 'A' && a.user_id === currentUser.id
  );
  const isProjectPm = !!(projectPmId && currentUser.id === projectPmId);
  const canToggle = isResponsible || currentUser.role === 'Management' || isProjectPm;
  const canApprove = task.status === 'completed' && task.requires_approval && !task.approved_by && (isAccountable || currentUser.role === 'Management');

  // Show assign button for RACI tasks with no Responsible user, if PM or Management
  const hasResponsible = task.assignments?.some((a) => a.raci_type === 'R' && a.user_id);
  const canAssign = isPmOrMgmt && task.source === 'raci_template' && !hasResponsible;

  const handleAssign = async () => {
    if (!assignUserId || !onAssign) return;
    setAssignLoading(true);
    try {
      await onAssign(task.id, assignUserId);
      setAssignOpen(false);
      setAssignUserId('');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleToggle = async () => {
    if (!canToggle) return;
    setLoading(true);
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await onStatusChange(task.id, newStatus);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      await onApprove(task.id);
    } finally {
      setLoading(false);
    }
  };

  const isZoho = task.source === 'zoho';
  const isRaciTemplate = task.source === 'raci_template';
  const pct = task.percent_complete || 0;

  // Group assignments by raci_type, filtering out nulls
  const validAssignments = (task.assignments || []).filter(a => a && a.raci_type && a.user_name);
  const byType = {};
  for (const a of validAssignments) {
    if (!byType[a.raci_type]) byType[a.raci_type] = [];
    if (!byType[a.raci_type].includes(a.user_name)) byType[a.raci_type].push(a.user_name);
  }

  return (
    <>
      <ListItem
        sx={{
          '&:hover': { bgcolor: canToggle ? 'action.hover' : 'inherit' },
          cursor: canToggle ? 'pointer' : 'default',
          alignItems: 'flex-start',
          borderLeft: isRaciTemplate ? '3px solid #7B1FA2' : 'none',
          pl: isRaciTemplate ? 1.5 : 2,
        }}
      >
        <Checkbox
          checked={task.status === 'completed'}
          onChange={handleToggle}
          disabled={!canToggle || loading}
          icon={<RadioButtonUncheckedIcon />}
          checkedIcon={<CheckCircleIcon />}
          sx={{ mr: 2, mt: 0.5 }}
        />
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body1"
                sx={{
                  textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                  color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
                }}
              >
                {task.name}
              </Typography>
              {isZoho && (
                <Tooltip title="Synced from Zoho Projects">
                  <Chip
                    label="Zoho"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 18, color: 'primary.main', borderColor: 'primary.light' }}
                  />
                </Tooltip>
              )}
              {isRaciTemplate && (
                <Tooltip title="RACI Template Task">
                  <Chip
                    label="RACI"
                    size="small"
                    sx={{
                      fontSize: '0.65rem',
                      height: 18,
                      bgcolor: '#7B1FA215',
                      color: '#7B1FA2',
                      border: '1px solid #7B1FA240',
                    }}
                  />
                </Tooltip>
              )}
              {task.status === 'completed' && task.approved_by && (
                <Chip label="Approved" size="small" color="success" />
              )}
              {canApprove && (
                <Button size="small" variant="outlined" onClick={handleApprove} disabled={loading}>
                  Approve
                </Button>
              )}
              {canAssign && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={(e) => { e.stopPropagation(); setAssignOpen(true); }}
                  sx={{ fontSize: '0.7rem', py: 0.25 }}
                >
                  + Assign
                </Button>
              )}
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              {/* RACI assignment rows */}
              {['R', 'A', 'C', 'I'].map(type =>
                byType[type] ? (
                  <Box key={type} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.25 }}>
                    <Tooltip title={RACI_LABELS[type]}>
                      <span><RaciChip type={type} /></span>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {byType[type].join(', ')}
                    </Typography>
                  </Box>
                ) : null
              )}
              {task.due_date && (
                <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                  Due: {format(new Date(task.due_date), 'MMM d, yyyy')}
                </Typography>
              )}
              {isZoho && pct > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{ flex: 1, height: 4, borderRadius: 2, maxWidth: 120 }}
                    color={pct === 100 ? 'success' : 'primary'}
                  />
                  <Typography variant="caption" color="text.secondary">{pct}%</Typography>
                </Box>
              )}
            </Box>
          }
        />
      </ListItem>
      <Divider component="li" />

      {/* Assign Responsible Dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Assign Responsible — {task.name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Assign to</InputLabel>
            <Select value={assignUserId} label="Assign to" onChange={(e) => setAssignUserId(e.target.value)}>
              {(allUsers || []).map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({u.role})</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={!assignUserId || assignLoading}>
            {assignLoading ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── Sage Financials Card ─────────────────────────────────────────────────────

const SageFinancials = ({ projectId }) => {
  const [sage, setSage] = useState(undefined); // undefined = loading, null = no data
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios
      .get(`/api/projects/${projectId}/sage`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setSage(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.response?.status === 403) {
            setSage(null);
          } else {
            setError('Failed to load Sage data');
            setSage(null);
          }
        }
      });
    return () => { cancelled = true; };
  }, [projectId]);

  if (sage === undefined) {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>Sage Financials</Typography>
        <Skeleton variant="rectangular" height={120} />
      </Paper>
    );
  }

  if (!sage || error) return null;

  const contractAmt   = parseFloat(sage.sage_contract_amount || 0);
  const laborEst      = parseFloat(sage.sage_labor_est || 0);
  const laborActual   = parseFloat(sage.sage_actual_labor_hours || 0);
  const jtdCost       = parseFloat(sage.sage_jtd_cost || 0);
  const totalEst      = parseFloat(sage.sage_total_estimate || 0);
  const materialCost  = parseFloat(sage.sage_material_cost || 0);
  const subCost       = parseFloat(sage.sage_subcontract_cost || 0);
  const billedVal     = parseFloat(sage.sage_billed || 0);
  const workBilled    = parseFloat(sage.sage_work_billed || 0);
  const laborCost     = parseFloat(sage.sage_actual_labor_cost || 0);

  const laborPct   = laborEst   > 0 ? (laborActual / laborEst) * 100 : 0;
  const costPct    = totalEst   > 0 ? Math.min(100, (jtdCost    / totalEst)    * 100) : 0;
  const billedPct  = contractAmt > 0 ? Math.min(100, (workBilled / contractAmt) * 100) : 0;

  const laborColor  = laborPct > 100 ? 'error' : laborPct > 90 ? 'error' : laborPct > 75 ? 'warning' : 'primary';
  const costColor   = costPct   > 90 ? 'error' : costPct   > 75 ? 'warning' : 'primary';
  const billedColor = billedPct > 90 ? 'success' : 'primary';

  const syncedAt = sage.sage_synced_at
    ? (() => { try { return format(new Date(sage.sage_synced_at), 'MMM d, yyyy h:mm a'); } catch { return sage.sage_synced_at; } })()
    : '—';

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">Sage Financials</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {sage.sage_job_type && (
            <Chip label={sage.sage_job_type} size="small" variant="outlined" />
          )}
          <Typography variant="caption" color="text.secondary">
            Synced: {syncedAt}
          </Typography>
        </Box>
      </Box>

      {/* Top row: Contract, PM, Salesman */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Contract Amount</Typography>
          <Typography variant="h5" fontWeight="bold" color="success.main">
            {contractAmt > 0 ? fmtCurrency(contractAmt) : '—'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Billed vs Contract</Typography>
          <Typography variant="h6" fontWeight="bold"
            color={contractAmt > 0 && workBilled > contractAmt ? 'error.main' : 'text.primary'}>
            {fmtCurrency(workBilled)}
            {contractAmt > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                / {fmtCurrency(contractAmt)}
              </Typography>
            )}
          </Typography>
          {contractAmt > 0 && (
            <LinearProgress
              variant="determinate"
              value={billedPct}
              color={billedColor}
              sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Sage PM</Typography>
          <Typography variant="body1" fontWeight="medium">{sage.sage_pm_name || '—'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Sage Salesman</Typography>
          <Typography variant="body1" fontWeight="medium">{sage.sage_salesman || '—'}</Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Labor Hours */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Labor Hours</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Estimated</Typography>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              {laborEst > 0 ? laborEst.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Actual (JTD)</Typography>
            <Typography variant="h6" fontWeight="bold" color={`${laborColor}.main`}>
              {laborActual > 0 ? laborActual.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">% Used</Typography>
            <Typography variant="h6" fontWeight="bold" color={`${laborColor}.main`}>
              {laborEst > 0 ? Math.round(laborPct) + '%' : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Labor Cost</Typography>
            <Typography variant="body1" fontWeight="medium">{laborCost > 0 ? fmtCurrency(laborCost) : '—'}</Typography>
          </Grid>
        </Grid>
        {laborEst > 0 && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, laborPct)}
            color={laborColor}
            sx={{ height: 8, borderRadius: 4 }}
          />
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Cost vs Estimate */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Job-to-Date Costs</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Total Estimate</Typography>
            <Typography variant="body1" fontWeight="medium">{totalEst > 0 ? fmtCurrency(totalEst) : '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">JTD Cost</Typography>
            <Typography variant="body1" fontWeight="bold" color={`${costColor}.main`}>
              {jtdCost > 0 ? fmtCurrency(jtdCost) : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Material Cost</Typography>
            <Typography variant="body1" fontWeight="medium">{materialCost > 0 ? fmtCurrency(materialCost) : '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Subcontract Cost</Typography>
            <Typography variant="body1" fontWeight="medium">{subCost > 0 ? fmtCurrency(subCost) : '—'}</Typography>
          </Grid>
        </Grid>
        {totalEst > 0 && (
          <LinearProgress
            variant="determinate"
            value={costPct}
            color={costColor}
            sx={{ height: 8, borderRadius: 4 }}
          />
        )}
        {totalEst > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {Math.round(costPct)}% of estimated budget consumed
          </Typography>
        )}
      </Box>

      {/* Billed Value row */}
      {(billedVal > 0 || workBilled > 0) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Scheduled Value (Billed)</Typography>
              <Typography variant="body1" fontWeight="medium">{billedVal > 0 ? fmtCurrency(billedVal) : '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Work Billed (JTD)</Typography>
              <Typography variant="body1" fontWeight="bold" color="success.main">
                {workBilled > 0 ? fmtCurrency(workBilled) : '—'}
              </Typography>
            </Grid>
            {sage.sage_est_start && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Est. Start</Typography>
                <Typography variant="body1" fontWeight="medium">{fmtDate(sage.sage_est_start)}</Typography>
              </Grid>
            )}
            {sage.sage_est_comp && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Est. Completion</Typography>
                <Typography variant="body1" fontWeight="medium">{fmtDate(sage.sage_est_comp)}</Typography>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Paper>
  );
};

// ─── Bin Tab ──────────────────────────────────────────────────────────────────

function fmtCurrencyBin(val) {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtNumBin(n) {
  if (n == null) return '—';
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

const CLASS_COLORS_BIN = {
  TV:  '#1565C0', SPK: '#2E7D32', CON: '#E65100', AMP: '#6A1B9A',
  SHD: '#00695C', LTG: '#F57F17', PRO: '#37474F', REC: '#C62828',
};

const BinTab = ({ projectId }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!projectId) return;
    const token = localStorage.getItem('token');
    axios.get(`/api/projects/${projectId}/bin`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <LinearProgress />;
  if (error)   return <Alert severity="warning">Could not load bin data: {error}</Alert>;
  if (!data || data.bins.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No material allocated to this project's bin</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Grand total */}
      <Box sx={{ display: 'flex', gap: 4, mb: 3, p: 2, bgcolor: 'primary.main', borderRadius: 2, color: 'white', flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Bin Value</Typography>
          <Typography variant="h5" fontWeight="bold">{fmtCurrencyBin(data.total_value)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Line Items</Typography>
          <Typography variant="h5" fontWeight="bold">{fmtNumBin(data.total_items)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Bin Locations</Typography>
          <Typography variant="h5" fontWeight="bold">{data.bins.length}</Typography>
        </Box>
      </Box>

      {data.bins.map((bin) => (
        <Accordion key={bin.bin_location} defaultExpanded={data.bins.length === 1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
              <Chip label={bin.bin_location} size="small" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
              <Typography variant="body2" color="text.secondary">Job #{bin.job_number}</Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 3 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Items</Typography>
                  <Typography variant="body2" fontWeight="bold">{bin.item_count}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Value</Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {fmtCurrencyBin(bin.total_value)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Item Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Class</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Value</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Avg Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>UOM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bin.items.map((item) => (
                    <TableRow key={item.itemcode} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="primary.main">
                          {item.itemcode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.itemdescription}</Typography>
                      </TableCell>
                      <TableCell>
                        {item.class && (
                          <Chip
                            label={item.class}
                            size="small"
                            sx={{
                              bgcolor: `${CLASS_COLORS_BIN[item.class] || '#616161'}20`,
                              color: CLASS_COLORS_BIN[item.class] || '#616161',
                              fontSize: '0.7rem', fontWeight: 600,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">{fmtNumBin(item.qtyonhand)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">{fmtCurrencyBin(item.valueonhand)}</Typography>
                      </TableCell>
                      <TableCell align="right">{fmtCurrencyBin(item.avgcost)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{item.stockinguom || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Summary row */}
                  <TableRow sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                      Totals — {bin.item_count} items
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtNumBin(bin.total_qty)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {fmtCurrencyBin(bin.total_value)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// ─── Purchase Orders Tab ──────────────────────────────────────────────────────

const PO_STATUS_COLORS = {
  O: { label: 'Open',     color: '#1565C0', bg: '#E3F2FD' },
  C: { label: 'Closed',   color: '#2E7D32', bg: '#E8F5E9' },
  P: { label: 'Partial',  color: '#E65100', bg: '#FFF3E0' },
};

function poStatusInfo(status) {
  if (!status) return { label: status || '—', color: '#616161', bg: '#F5F5F5' };
  const s = (status || '').trim().toUpperCase().charAt(0);
  return PO_STATUS_COLORS[s] || { label: status, color: '#616161', bg: '#F5F5F5' };
}

function poReceiptColor(po) {
  const orig = parseFloat(po.porevisedamt || po.poorigamount) || 0;
  const recv = parseFloat(po.poreceivedamount) || 0;
  if (orig === 0) return 'default';
  const pct = recv / orig;
  if (pct >= 0.99) return 'success';
  if (pct > 0)     return 'warning';
  return 'error';
}

const PurchaseOrdersTab = ({ projectId }) => {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [statusFlt, setStatusFlt] = useState('ALL');

  useEffect(() => {
    if (!projectId) return;
    const token = localStorage.getItem('token');
    axios.get(`/api/projects/${projectId}/purchase-orders`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <LinearProgress />;
  if (error)   return <Alert severity="warning">Could not load purchase orders: {error}</Alert>;
  if (!data || data.purchase_orders.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <ShoppingCartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No purchase orders found for this project</Typography>
      </Box>
    );
  }

  const filtered = data.purchase_orders.filter((po) => {
    if (statusFlt === 'ALL') return true;
    const s = (po.postatus || '').trim().toUpperCase().charAt(0);
    return s === statusFlt;
  });

  return (
    <Box>
      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 4, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Total POs</Typography>
          <Typography variant="h6" fontWeight="bold">{data.purchase_orders.length}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Total PO Value</Typography>
          <Typography variant="h6" fontWeight="bold" color="primary.main">
            {fmtCurrencyBin(data.total_value)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Total Received</Typography>
          <Typography variant="h6" fontWeight="bold" color="success.main">
            {fmtCurrencyBin(data.total_received)}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFlt} label="Status" onChange={(e) => setStatusFlt(e.target.value)}>
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="O">Open</MenuItem>
              <MenuItem value="C">Closed</MenuItem>
              <MenuItem value="P">Partial</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {filtered.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>No POs match the selected filter.</Typography>
      )}

      {filtered.map((po) => {
        const statusInfo = poStatusInfo(po.postatus);
        const recvChipColor = poReceiptColor(po);
        const poValue = parseFloat(po.porevisedamt || po.poorigamount) || 0;

        return (
          <Accordion key={po.ponumber} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight="bold" fontFamily="monospace" color="primary.main" sx={{ minWidth: 90 }}>
                  {po.ponumber}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                  {po.podate ? new Date(po.podate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ flex: 1, minWidth: 160 }}>
                  {po.supname || po.supplier || '—'}
                </Typography>
                {po.podesc && (
                  <Tooltip title={po.podesc}>
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {po.podesc}
                    </Typography>
                  </Tooltip>
                )}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Chip
                    label={statusInfo.label}
                    size="small"
                    sx={{ bgcolor: statusInfo.bg, color: statusInfo.color, fontWeight: 600, fontSize: '0.7rem' }}
                  />
                  <Chip
                    label={recvChipColor === 'success' ? 'Fully Received' : recvChipColor === 'warning' ? 'Partial' : 'Not Received'}
                    size="small"
                    color={recvChipColor}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                  <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 90, textAlign: 'right' }}>
                    {fmtCurrencyBin(poValue)}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Item Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Qty Ordered</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Qty Received</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Unit Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Extended Value</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {po.lines.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="caption" color="text.secondary">No line items</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {po.lines.map((line, idx) => {
                      const qty   = parseFloat(line.origqtyordered) || 0;
                      const recv  = parseFloat(line.qtyrecd) || 0;
                      const pct   = qty > 0 ? recv / qty : 0;
                      const rowBg = pct >= 0.99 ? '#F1F8E9' : pct > 0 ? '#FFF8E1' : undefined;
                      return (
                        <TableRow key={idx} sx={{ bgcolor: rowBg }}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                              {line.itemcode || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{line.shortdescription || '—'}</Typography>
                          </TableCell>
                          <TableCell align="right">{fmtNumBin(line.origqtyordered)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={pct >= 0.99 ? 'success.main' : pct > 0 ? 'warning.main' : 'error.main'}
                              fontWeight={recv > 0 ? 'bold' : 'normal'}
                            >
                              {fmtNumBin(line.qtyrecd)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtCurrencyBin(line.unitrate)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {fmtCurrencyBin(line.revextendedvalue || line.origextendedvalue)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {line.linestatus && (
                              <Chip label={line.linestatus} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

// ─── Client Documents Section (rendered inside Proposal tab) ─────────────

const DOC_CATEGORIES = ['Proposal', 'Acceptance / Agreement', 'Change Order', 'Contract', 'Scope of Work', 'Other'];

const DOC_CATEGORY_ICONS = {
  'Proposal': { color: '#e65100', icon: '📝' },
  'Acceptance / Agreement': { color: '#2E7D32', icon: '✅' },
  'Change Order': { color: '#6A1B9A', icon: '🔄' },
  'Contract': { color: '#1565C0', icon: '📜' },
  'Scope of Work': { color: '#00695C', icon: '📋' },
  'Other': { color: '#757575', icon: '📄' },
};

const ClientDocuments = ({ projectId }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('Proposal');
  const [uploadNotes, setUploadNotes] = useState('');
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = React.useRef(null);

  const loadDocs = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProjectDocuments(projectId);
      setDocuments(res.data.documents || []);
      setByCategory(res.data.by_category || {});
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', uploadCategory);
      if (uploadNotes.trim()) formData.append('notes', uploadNotes.trim());
      await uploadProjectDocument(projectId, formData);
      setUploadNotes('');
      loadDocs();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    try {
      await deleteProjectDocument(projectId, docId);
      setDeleteConfirm(null);
      loadDocs();
    } catch (err) {
      setError('Failed to delete');
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderTop: '3px solid #0D2C54' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ color: '#0D2C54' }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#0D2C54' }}>Client Documents</Typography>
          {documents.length > 0 && (
            <Chip label={documents.length} size="small" sx={{ bgcolor: '#0D2C54', color: '#fff', fontWeight: 700, height: 22 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={uploadCategory}
              onChange={e => setUploadCategory(e.target.value)}
              sx={{ fontSize: '0.8rem', height: 32 }}
            >
              {DOC_CATEGORIES.map(c => (
                <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>
                  {DOC_CATEGORY_ICONS[c]?.icon} {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            sx={{ bgcolor: '#0D2C54', '&:hover': { bgcolor: '#15253e' }, textTransform: 'none' }}
          >
            {uploading ? 'Uploading…' : 'Upload Document'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {documents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <DescriptionIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No documents uploaded yet</Typography>
          <Typography variant="caption" color="text.secondary">Upload proposals, agreements, change orders, and other client documents</Typography>
        </Box>
      ) : (
        DOC_CATEGORIES.filter(cat => byCategory[cat]?.length > 0).map(cat => {
          const catCfg = DOC_CATEGORY_ICONS[cat];
          return (
            <Box key={cat} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: catCfg.color, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {catCfg.icon} {cat} ({byCategory[cat].length})
              </Typography>
              <Table size="small">
                <TableBody>
                  {byCategory[cat].map(doc => (
                    <TableRow key={doc.id} hover>
                      <TableCell sx={{ py: 0.75, width: 30 }}>
                        <PdfIcon sx={{ fontSize: 18, color: doc.mime_type === 'application/pdf' ? '#C62828' : '#1565C0' }} />
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <a
                          href={`/uploads/documents/${doc.filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: '#1A5490', fontWeight: 600, fontSize: '0.85rem' }}
                        >
                          {doc.original_name}
                          <OpenInNewIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle' }} />
                        </a>
                        {doc.notes && <Typography variant="caption" color="text.secondary" display="block">{doc.notes}</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 0.75, fontSize: '0.75rem', color: 'text.secondary' }}>
                        {fmtSize(doc.file_size)}
                      </TableCell>
                      <TableCell sx={{ py: 0.75, fontSize: '0.75rem', color: 'text.secondary' }}>
                        {doc.uploaded_by_name} · {fmtDate(doc.created_at)}
                      </TableCell>
                      <TableCell sx={{ py: 0.75, width: 40 }}>
                        {(doc.uploaded_by === user?.id || user?.role === 'Management') && (
                          deleteConfirm === doc.id ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" color="error" onClick={() => handleDelete(doc.id)}><CheckIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => setDeleteConfirm(null)}><CloseIcon fontSize="small" /></IconButton>
                            </Box>
                          ) : (
                            <IconButton size="small" onClick={() => setDeleteConfirm(doc.id)}><DeleteIcon fontSize="small" /></IconButton>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          );
        })
      )}
    </Paper>
  );
};

// ─── Unbillable Labor Tab ─────────────────────────────────────────────────────

// Renders alternate names (street address, city, LLC, AR code, nickname,
// etc.) plus sibling projects so a PM can answer "is this the same as
// 10 Coachman's Court / Red Rock?" in one glance. Tom Clancy 2026-04-30.

const ALIAS_TYPE_LABELS = {
  address:      { label: 'Address',      icon: '📍', color: '#1565C0' },
  city:         { label: 'City / Town',  icon: '🏙️', color: '#0277BD' },
  site_label:   { label: 'Site Label',   icon: '🏷️',  color: '#5D4037' },
  client:       { label: 'Client / LLC', icon: '🏢', color: '#7B1FA2' },
  family:       { label: 'Family',       icon: '👪', color: '#00695C' },
  ar_code:      { label: 'AR Code',      icon: '#',  color: '#E65100' },
  job_number:   { label: 'Job Number',   icon: '#',  color: '#E65100' },
  dtools_id:    { label: 'D-Tools ID',   icon: '⚙️', color: '#455A64' },
  zoho_account: { label: 'Zoho Account', icon: '📇', color: '#2E7D32' },
  other:        { label: 'Other',        icon: '—', color: '#616161' },
};

const AlsoKnownAsPanel = ({ projectId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newAlias, setNewAlias] = useState({ alias_type: 'address', alias_value: '' });
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProjectAliases(projectId);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load aliases');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newAlias.alias_value.trim() || saving) return;
    setSaving(true);
    try {
      await addProjectAlias(projectId, newAlias);
      setNewAlias({ alias_type: 'address', alias_value: '' });
      setAdding(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add alias');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (aliasId) => {
    try {
      await deleteProjectAlias(projectId, aliasId);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove alias');
    }
  };

  if (loading || !data) return null;
  const aliases = data.aliases || [];
  const siblings = data.sibling_projects || [];
  if (aliases.length === 0 && siblings.length === 0 && !adding) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          No alternate names recorded. Add one if this project is known by a different address, LLC, or nickname.
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
          Add alias
        </Button>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderLeft: '4px solid #1565C0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: collapsed ? 0 : 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#1565C0' }}>
            🔗 Also Known As
          </Typography>
          <Chip label={`${aliases.length} alias${aliases.length === 1 ? '' : 'es'}`} size="small" variant="outlined" />
          {siblings.length > 0 && (
            <Chip label={`${siblings.length} sibling project${siblings.length === 1 ? '' : 's'}`} size="small" variant="outlined" color="primary" />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!collapsed && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} disabled={adding}>
              Add
            </Button>
          )}
          <Button size="small" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? 'Show' : 'Hide'}
          </Button>
        </Box>
      </Box>

      {!collapsed && (
        <>
          {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

          {aliases.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: siblings.length ? 2 : 0 }}>
              {aliases.map((a) => {
                const cfg = ALIAS_TYPE_LABELS[a.alias_type] || ALIAS_TYPE_LABELS.other;
                return (
                  <Tooltip
                    key={a.id}
                    title={
                      <Box sx={{ fontSize: '0.75rem' }}>
                        <div><strong>{cfg.label}</strong></div>
                        {a.notes && <div style={{ fontStyle: 'italic' }}>{a.notes}</div>}
                        <div style={{ opacity: 0.85, marginTop: 4 }}>
                          source: {a.source}{a.created_by_name ? ` · by ${a.created_by_name}` : ''}
                        </div>
                      </Box>
                    }
                  >
                    <Chip
                      label={
                        <span>
                          <span style={{ marginRight: 6, fontSize: '0.75rem', color: cfg.color, fontWeight: 600 }}>
                            {cfg.icon} {cfg.label}:
                          </span>
                          {a.alias_value}
                        </span>
                      }
                      size="small"
                      variant="outlined"
                      onDelete={() => handleDelete(a.id)}
                      sx={{
                        bgcolor: `${cfg.color}08`,
                        borderColor: `${cfg.color}40`,
                        '& .MuiChip-deleteIcon': { fontSize: 16, color: 'text.disabled' },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          )}

          {adding && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label="Type"
                value={newAlias.alias_type}
                onChange={(e) => setNewAlias((a) => ({ ...a, alias_type: e.target.value }))}
                sx={{ minWidth: 160 }}
                SelectProps={{ native: true }}
              >
                {Object.entries(ALIAS_TYPE_LABELS).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Alias value"
                placeholder="e.g. 10 Coachman's Court, Red Rock Investments LLC, REDR01…"
                value={newAlias.alias_value}
                onChange={(e) => setNewAlias((a) => ({ ...a, alias_value: e.target.value }))}
                sx={{ flex: '1 1 320px' }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              />
              <Button variant="contained" size="small" disabled={saving || !newAlias.alias_value.trim()} onClick={handleAdd}>
                {saving ? 'Saving…' : 'Add'}
              </Button>
              <Button size="small" onClick={() => { setAdding(false); setNewAlias({ alias_type: 'address', alias_value: '' }); }}>
                Cancel
              </Button>
            </Box>
          )}

          {siblings.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                Sibling projects (same site, AR code, or legal entity)
              </Typography>
              <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {siblings.map((s) => {
                  const job = s.primary_job_number || s.sage_job_number;
                  const ar = s.sage_ar_customer || s.ar_number;
                  const client = s.sage_customer_name || s.client_name;
                  const addr = s.project_address || s.site_address;
                  return (
                    <Box
                      key={s.id}
                      onClick={() => navigate(`/projects/${s.id}`)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1,
                        cursor: 'pointer',
                        bgcolor: 'grey.50',
                        '&:hover': { bgcolor: 'primary.50' },
                      }}
                    >
                      <Typography variant="body2" fontWeight="medium" sx={{ flex: '0 0 auto' }}>
                        {s.name}
                      </Typography>
                      <Box sx={{ flex: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {job && <Typography variant="caption" color="text.secondary">Job {job}</Typography>}
                        {ar && <Typography variant="caption" color="text.secondary">AR {ar}</Typography>}
                        {client && <Typography variant="caption" color="text.secondary">{client}</Typography>}
                        {addr && <Typography variant="caption" color="text.secondary">{addr}</Typography>}
                      </Box>
                      <Chip
                        label={s.status || 'active'}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 18, fontSize: '0.65rem',
                          color: s.status === 'completed' ? 'success.dark' : 'primary.dark',
                          borderColor: s.status === 'completed' ? 'success.main' : 'primary.main',
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

const UnbillableLaborTab = ({ projectId }) => {
  const { user } = useAuth();
  const canAssignOthers =
    user?.role === 'PM' || user?.role === 'Management' || user?.role === 'Field Operations' ||
    (Array.isArray(user?.roles) && (user.roles.includes('PM') || user.roles.includes('Management') || user.roles.includes('Field Operations')));

  const [entries, setEntries] = React.useState([]);
  const [totalHours, setTotalHours] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({
    entry_date: new Date().toISOString().split('T')[0],
    hours: '',
    reason: '',
    worker_user_id: null,  // null means "the logged-in user"
  });
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);
  const [activeUsers, setActiveUsers] = React.useState([]);
  const [usersLoaded, setUsersLoaded] = React.useState(false);

  const loadEntries = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getUnbillableLabor(projectId);
      setEntries(res.data.entries || []);
      setTotalHours(res.data.total_hours || 0);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { loadEntries(); }, [loadEntries]);

  // Load active users lazily the first time a PM opens the form, so the
  // worker picker has options to choose from.
  React.useEffect(() => {
    if (!showForm || !canAssignOthers || usersLoaded) return;
    getUsers()
      .then((res) => {
        const list = (res.data || [])
          .filter((u) => u.active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setActiveUsers(list);
      })
      .catch(() => { /* non-fatal — falls back to logged-in user */ })
      .finally(() => setUsersLoaded(true));
  }, [showForm, canAssignOthers, usersLoaded]);

  const handleSubmit = async () => {
    if (!form.entry_date || !form.hours || !form.reason?.trim()) return;
    setSaving(true);
    try {
      await addUnbillableLabor(projectId, {
        entry_date: form.entry_date,
        hours: parseFloat(form.hours),
        reason: form.reason.trim(),
        worker_user_id: form.worker_user_id || undefined,
      });
      setForm({
        entry_date: new Date().toISOString().split('T')[0],
        hours: '',
        reason: '',
        worker_user_id: null,
      });
      setShowForm(false);
      loadEntries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await deleteUnbillableLabor(projectId, entryId);
      setDeleteConfirm(null);
      loadEntries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete entry');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyOffIcon color="warning" />
          <Typography variant="h6" fontWeight="bold">Unbillable Labor</Typography>
          {totalHours > 0 && (
            <Chip label={`${totalHours.toFixed(1)} hrs total`} size="small" color="warning" variant="outlined" />
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(!showForm)}
          sx={{ bgcolor: '#0D2C54', '&:hover': { bgcolor: '#15253e' } }}
        >
          Log Unbillable Time
        </Button>
      </Box>

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Log any work performed on this project that cannot be billed to the client. Include the date, time spent, and the reason the work is non-billable.
        </Typography>
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* New Entry Form */}
      <Collapse in={showForm}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'warning.main' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>New Unbillable Labor Entry</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            {canAssignOthers && (
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  size="small"
                  options={activeUsers}
                  loading={!usersLoaded}
                  getOptionLabel={(opt) => (opt && opt.name) ? `${opt.name}${opt.role ? ` — ${opt.role}` : ''}` : ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  value={activeUsers.find((u) => u.id === form.worker_user_id) || null}
                  onChange={(_, val) => setForm((f) => ({ ...f, worker_user_id: val?.id || null }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Worker"
                      placeholder={`Default: ${user?.name || 'me'}`}
                      helperText={form.worker_user_id ? 'Logging on their behalf' : 'Defaults to you'}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={canAssignOthers ? 2 : 3}>
              <TextField
                label="Date"
                type="date"
                size="small"
                fullWidth
                value={form.entry_date}
                onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={canAssignOthers ? 1 : 2}>
              <TextField
                label="Hours"
                type="number"
                size="small"
                fullWidth
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                inputProps={{ min: 0.25, step: 0.25 }}
                placeholder="e.g. 2.5"
              />
            </Grid>
            <Grid item xs={12} sm={canAssignOthers ? 4 : 5}>
              <TextField
                label="Reason (why is this non-billable?)"
                size="small"
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Rework due to incorrect spec, warranty callback, internal meeting..."
              />
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                disabled={saving || !form.hours || !form.reason?.trim()}
                onClick={handleSubmit}
                sx={{ bgcolor: '#0D2C54', '&:hover': { bgcolor: '#15253e' } }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button size="small" onClick={() => { setShowForm(false); setForm((f) => ({ ...f, worker_user_id: null })); }}>Cancel</Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Entries Table */}
      {entries.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <MoneyOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No unbillable labor entries yet</Typography>
          <Typography variant="caption" color="text.secondary">Click "Log Unbillable Time" to add the first entry</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Person</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Hours</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center" width={60}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>{fmtDate(entry.entry_date)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{entry.user_name}</Typography>
                      {entry.user_role && (
                        <Chip label={entry.user_role} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                      {entry.logged_by_user_id && entry.logged_by_user_id !== entry.user_id && entry.logged_by_name && (
                        <Tooltip title={`Entry recorded by ${entry.logged_by_name}`}>
                          <Chip
                            label={`logged by ${entry.logged_by_name}`}
                            size="small"
                            sx={{ fontSize: '0.65rem', bgcolor: 'grey.100', color: 'text.secondary' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="warning.dark">
                      {parseFloat(entry.hours).toFixed(1)}h
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{entry.reason}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {(entry.user_id === user?.id || entry.logged_by_user_id === user?.id || canAssignOthers) && (
                      deleteConfirm === entry.id ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(entry.id)}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setDeleteConfirm(null)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton size="small" color="default" onClick={() => setDeleteConfirm(entry.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: 'warning.50' }}>
                <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Unbillable</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'warning.dark' }}>{totalHours.toFixed(1)}h</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── TimelineTab ──────────────────────────────────────────────────────────────

const TIMELINE_PHASE_NAMES = [
  'Proposal',
  'Engineering & Kick-off',
  'Rough-in',
  'Pre-Final',
  'Final',
  'Final Billing',
  'AfterCare',
];

const TimelineTab = ({ projectId, onSaved }) => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [phases, setPhases] = useState([]);
  const [projectStart, setProjectStart] = useState('');
  const [projectEnd, setProjectEnd] = useState('');
  const [reviewedBy, setReviewedBy] = useState(null);
  const [lastReviewed, setLastReviewed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  // Local editable state: keyed by phase id.
  const [edits, setEdits] = useState({});

  // Which fields the user has actually touched in this session, keyed by
  // phase id. Tom Clancy 2026-07-19: without this, the client was
  // re-sending every phase's actual dates on every save (including
  // untouched ones), which was silently overwriting DB values on mobile
  // Safari (where the native date picker races with the Save tap) and
  // tripping the server's completion-vs-start validator on unrelated
  // phases. Now the payload only includes fields the user actually
  // changed — nothing else round-trips through the client.
  const [dirty, setDirty] = useState({}); // { [phaseId]: Set('estimated_start' | 'started_at' | ...) }

  // Timeline edit access: Management, PM, Field Operations, Sales, plus
  // individually whitelisted users (Carmela Verni, id 18 — Tom Clancy 2026-07-08).
  const TIMELINE_EDIT_ROLES = ['PM', 'Management', 'Field Operations', 'Sales'];
  const TIMELINE_EDIT_USER_IDS = new Set([18]);
  const canEdit =
    TIMELINE_EDIT_ROLES.includes(user?.role) || TIMELINE_EDIT_USER_IDS.has(user?.id);

  useEffect(() => {
    loadTimeline();
  }, [projectId]);

  const loadTimeline = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/projects/${projectId}/timeline`, { headers });
      const data = res.data;
      setPhases(data.phases || []);
      // Project-level dates
      const proj = data.project || {};
      setProjectStart(proj.installation_start_date ? proj.installation_start_date.substring(0, 10) : '');
      setProjectEnd(proj.projected_completion_date ? proj.projected_completion_date.substring(0, 10) : '');
      setLastReviewed(proj.timeline_last_reviewed || null);
      setReviewedBy(proj.reviewed_by_name || null);
      // Seed edits from loaded data
      const initial = {};
      (data.phases || []).forEach(p => {
        initial[p.id] = {
          estimated_start: p.estimated_start ? p.estimated_start.substring(0, 10) : '',
          estimated_end:   p.estimated_end   ? p.estimated_end.substring(0, 10)   : '',
          timeline_notes:  p.timeline_notes  || '',
          started_at:      p.started_at     ? p.started_at.substring(0, 10)      : '',
          completed_at:    p.completed_at   ? p.completed_at.substring(0, 10)    : '',
        };
      });
      setEdits(initial);
      setDirty({}); // fresh load → nothing is dirty yet
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (phaseId, field, value) => {
    setEdits(prev => ({ ...prev, [phaseId]: { ...prev[phaseId], [field]: value } }));
    setDirty(prev => {
      const next = { ...prev };
      const cur = next[phaseId] ? new Set(next[phaseId]) : new Set();
      cur.add(field);
      next[phaseId] = cur;
      return next;
    });
  };

  // Build the per-phase save payload. Only includes fields the user
  // actually touched in this session (dirty set). Estimated dates and
  // notes are always sent (small, harmless, and if they're empty they
  // legitimately mean "clear"). Actual dates are ONLY sent when touched,
  // and we set the `touched_*` flags so the server knows to treat them
  // as an intentional update (see server-side comment 2026-07-19).
  const buildPhasesPayload = () => phases.map((p) => {
    const e = edits[p.id] || {};
    const d = dirty[p.id] || new Set();
    const payload = {
      phase_id:        p.id,
      estimated_start: e.estimated_start || null,
      estimated_end:   e.estimated_end   || null,
      timeline_notes:  e.timeline_notes  || null,
    };
    if (d.has('started_at')) {
      payload.started_at = e.started_at || null;
      payload.touched_started_at = true;
    }
    if (d.has('completed_at')) {
      payload.completed_at = e.completed_at || null;
      payload.touched_completed_at = true;
    }
    return payload;
  });

  // Auto-save just the project-level dates (no full timeline save required)
  const autoSaveProjectDates = async (start, end) => {
    try {
      await axios.put(
        `/api/projects/${projectId}/timeline`,
        { project_start: start || null, project_end: end || null, phases: buildPhasesPayload() },
        { headers }
      );
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Auto-save project dates failed:', err);
    }
  };

  const handleSave = async () => {
    // Tom Clancy 2026-07-19: on iOS Safari, tapping "Save Timeline" while
    // a native date picker is still open would submit before the picker's
    // value committed — silently sending nulls. Force any focused input
    // to blur first so React state is up to date before we build the
    // payload.
    try {
      if (typeof document !== 'undefined' && document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    } catch (_) { /* no-op */ }
    // Yield one tick so the resulting onChange (if any) lands in state
    // before we snapshot edits.
    await new Promise((r) => setTimeout(r, 0));

    setSaving(true);
    try {
      const phasesPayload = buildPhasesPayload();
      const res = await axios.put(
        `/api/projects/${projectId}/timeline`,
        { project_start: projectStart || null, project_end: projectEnd || null, phases: phasesPayload },
        { headers }
      );

      // Verify persistence. Compare what we intended to save against what
      // the server echoes back. If a field we set doesn't match the
      // returned row, surface it instead of showing a lying "saved" toast.
      const returned = res.data && Array.isArray(res.data.phases) ? res.data.phases : [];
      const returnedById = new Map(returned.map(r => [r.id, r]));
      const mismatches = [];
      for (const sent of phasesPayload) {
        const r = returnedById.get(sent.phase_id);
        if (!r) continue;
        const norm = (v) => (v ? String(v).substring(0, 10) : '');
        // Estimated dates: always compared (client always sends them).
        if (norm(sent.estimated_start) !== norm(r.estimated_start)) {
          mismatches.push({ phase_id: sent.phase_id, field: 'estimated_start' });
        }
        if (norm(sent.estimated_end) !== norm(r.estimated_end)) {
          mismatches.push({ phase_id: sent.phase_id, field: 'estimated_end' });
        }
        // Actual dates: only compared when we explicitly claimed to touch them.
        if (sent.touched_started_at && norm(sent.started_at) !== norm(r.started_at)) {
          mismatches.push({ phase_id: sent.phase_id, field: 'started_at' });
        }
        if (sent.touched_completed_at && norm(sent.completed_at) !== norm(r.completed_at)) {
          mismatches.push({ phase_id: sent.phase_id, field: 'completed_at' });
        }
      }

      const phaseErrors = (res.data && Array.isArray(res.data.phase_errors)) ? res.data.phase_errors : [];

      if (phaseErrors.length && !mismatches.length) {
        // Partial success — most phases saved, some rejected for validation reasons.
        const first = phaseErrors[0];
        const label = first.phase_name ? `"${first.phase_name}"` : `Phase ${first.phase_id}`;
        setSnackMsg(`Saved, but ${label} was skipped: ${first.reason}`);
      } else if (mismatches.length) {
        setSnackMsg(`Save didn't stick for ${mismatches.length} field(s). Try again — if it keeps failing, take a screenshot and send it to Tom.`);
      } else {
        setSnackMsg('Timeline saved successfully!');
      }
      setSnackOpen(true);
      await loadTimeline();
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Failed to save timeline:', err);
      const serverMsg = err && err.response && err.response.data && err.response.data.error;
      setSnackMsg(serverMsg ? `Failed to save timeline: ${serverMsg}` : 'Failed to save timeline. Please try again.');
      setSnackOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const getPhaseStatusIcon = (phase) => {
    if (phase.status === 'completed' || phase.completed_at) {
      return <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 22 }} />;
    }
    if (phase.status === 'in_progress' || phase.started_at) {
      return <CircleIcon sx={{ color: 'primary.main', fontSize: 22 }} />;
    }
    return <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 22 }} />;
  };

  const isOverdue = (actualDate, estimatedDate) => {
    if (!actualDate || !estimatedDate) return false;
    return new Date(actualDate) > new Date(estimatedDate);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Project-level dates */}
      {canEdit && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            Project Dates
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Project Start Date"
                type="date"
                size="small"
                fullWidth
                value={projectStart}
                onChange={e => setProjectStart(e.target.value)}
                onBlur={e => { if (e.target.value !== projectStart) return; autoSaveProjectDates(projectStart, projectEnd); }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Project Completion Date"
                type="date"
                size="small"
                fullWidth
                value={projectEnd}
                onChange={e => {
                  const val = e.target.value;
                  setProjectEnd(val);
                  // Auto-save after a short delay to capture the value
                  setTimeout(() => autoSaveProjectDates(projectStart, val), 300);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Last reviewed banner */}
      {lastReviewed && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Last reviewed by <strong>{reviewedBy || 'Unknown'}</strong> on{' '}
            {format(new Date(lastReviewed), 'MMM d, yyyy h:mm a')}
          </Typography>
        </Box>
      )}

      {/* Phase timeline */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {phases.map((phase, idx) => {
          const edit = edits[phase.id] || {};
          const startOverdue = isOverdue(phase.started_at, edit.estimated_start || phase.estimated_start);
          const endOverdue   = isOverdue(phase.completed_at, edit.estimated_end || phase.estimated_end);

          return (
            <Box key={phase.id} sx={{ display: 'flex', gap: 2 }}>
              {/* Left: connector line + icon */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
                <Box sx={{ mt: 1.5 }}>{getPhaseStatusIcon(phase)}</Box>
                {idx < phases.length - 1 && (
                  <Box sx={{ flex: 1, width: 2, bgcolor: 'grey.300', mt: 0.5, mb: -1 }} />
                )}
              </Box>

              {/* Right: card */}
              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  p: 2,
                  mb: 1,
                  borderColor: phase.status === 'completed' || phase.completed_at
                    ? 'success.light'
                    : phase.started_at ? 'primary.light' : 'grey.300',
                }}
              >
                {/* Phase header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Phase {phase.phase_number}: {TIMELINE_PHASE_NAMES[phase.phase_number - 1] || phase.name}
                  </Typography>
                  {(phase.status === 'completed' || phase.completed_at) && (
                    <Chip label="Completed" size="small" color="success" />
                  )}
                  {phase.started_at && !phase.completed_at && (
                    <Chip label="In Progress" size="small" color="primary" />
                  )}
                  {!phase.started_at && (
                    <Chip label="Not Started" size="small" variant="outlined" sx={{ color: 'text.disabled', borderColor: 'grey.300' }} />
                  )}
                </Box>

                <Grid container spacing={2}>
                  {/* Estimated dates */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Est. Start
                    </Typography>
                    {canEdit ? (
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={edit.estimated_start || ''}
                        onChange={e => handleChange(phase.id, 'estimated_start', e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    ) : (
                      <Typography variant="body2">{edit.estimated_start ? fmtDate(edit.estimated_start) : '—'}</Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Est. End
                    </Typography>
                    {canEdit ? (
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={edit.estimated_end || ''}
                        onChange={e => handleChange(phase.id, 'estimated_end', e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    ) : (
                      <Typography variant="body2">{edit.estimated_end ? fmtDate(edit.estimated_end) : '—'}</Typography>
                    )}
                  </Grid>

                  {/* Actual dates — editable for PMs/Mgmt/Field Ops. Tom
                       Clancy 2026-04-30: PMs were unable to record real
                       phase start / completion dates from this tab. */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Actual Start
                      {startOverdue && (
                        <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                          (late)
                        </Typography>
                      )}
                    </Typography>
                    {canEdit ? (
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={edit.started_at || ''}
                        onChange={e => handleChange(phase.id, 'started_at', e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={startOverdue ? { '& .MuiInputBase-input': { color: 'error.main', fontWeight: 600 } } : undefined}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color={startOverdue ? 'error.main' : 'text.primary'}
                        fontWeight={startOverdue ? 'bold' : 'normal'}
                      >
                        {edit.started_at ? fmtDate(edit.started_at) : '—'}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Actual Completion
                      {endOverdue && (
                        <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                          (late)
                        </Typography>
                      )}
                    </Typography>
                    {canEdit ? (
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={edit.completed_at || ''}
                        onChange={e => handleChange(phase.id, 'completed_at', e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={endOverdue ? { '& .MuiInputBase-input': { color: 'error.main', fontWeight: 600 } } : undefined}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color={endOverdue ? 'error.main' : 'text.primary'}
                        fontWeight={endOverdue ? 'bold' : 'normal'}
                      >
                        {edit.completed_at ? fmtDate(edit.completed_at) : '—'}
                      </Typography>
                    )}
                  </Grid>

                  {/* Notes */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Timeline Notes
                    </Typography>
                    {canEdit ? (
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Add timeline notes for this phase…"
                        value={edit.timeline_notes || ''}
                        onChange={e => handleChange(phase.id, 'timeline_notes', e.target.value)}
                      />
                    ) : (
                      <Typography variant="body2" color={edit.timeline_notes ? 'text.primary' : 'text.disabled'}>
                        {edit.timeline_notes || 'No notes'}
                      </Typography>
                    )}
                  </Grid>

                  {/* Last updated by */}
                  {phase.updated_by_name && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.disabled">
                        Last updated by {phase.updated_by_name} on{' '}
                        {phase.timeline_updated_at ? format(new Date(phase.timeline_updated_at), 'MMM d, yyyy') : '—'}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          );
        })}
      </Box>

      {/* Save button */}
      {canEdit && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CalendarTodayIcon />}
          >
            {saving ? 'Saving…' : 'Save Timeline'}
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMsg}
        action={
          <IconButton size="small" color="inherit" onClick={() => setSnackOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

// ─── Messaging helpers (shared with Messaging.js style) ───────────────────────

function pmRenderContent(text) {
  if (!text) return '';
  let html = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  html = html.replace(/`(.*?)`/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;font-family:monospace;font-size:0.85em">$1</code>');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

function pmFormatMsgTime(dateStr) {
  const d = new Date(dateStr);
  if (isToday(d)) return dateFnsFormat(d, 'h:mm a');
  if (isYesterday(d)) return `Yesterday ${dateFnsFormat(d, 'h:mm a')}`;
  return dateFnsFormat(d, 'MMM d, h:mm a');
}

function PmUserAvatar({ name, size = 36 }) {
  const initials = name
    ? name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  const colors = ['#1A5490', '#1565c0', '#2e7d32', '#6a1b9a', '#c62828', '#e65100', '#00695c'];
  const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: colors[colorIndex], fontSize: size * 0.38 }}>
      {initials}
    </Avatar>
  );
}

function PmMessageBubble({ message, prevMessage, currentUserId }) {
  const isMine = message.user_id === currentUserId;
  const showHeader =
    !prevMessage ||
    prevMessage.user_id !== message.user_id ||
    new Date(message.created_at) - new Date(prevMessage.created_at) > 5 * 60 * 1000;

  return (
    <Box sx={{ mb: showHeader ? 1.5 : 0.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ width: 36, flexShrink: 0 }}>
        {showHeader && <PmUserAvatar name={message.user_name} size={36} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {showHeader && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
            <Typography variant="body2" fontWeight={700} color={isMine ? '#1A5490' : 'text.primary'}>
              {isMine ? 'You' : message.user_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pmFormatMsgTime(message.created_at)}
            </Typography>
          </Box>
        )}
        <Typography
          variant="body2"
          component="div"
          sx={{ lineHeight: 1.5, wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: pmRenderContent(message.content) }}
        />
      </Box>
    </Box>
  );
}

// ─── Add Members Dialog ────────────────────────────────────────────────────────

function AddMembersDialog({ open, onClose, channelId, onMembersAdded }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getMessagingUsers()
      .then((res) => setUsers(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    setSelected([]);
  }, [open]);

  const toggle = (userId) => {
    setSelected((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSave = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    try {
      await addChannelMembers(channelId, selected);
      onMembersAdded?.();
      onClose();
    } catch (err) {
      console.error('Failed to add members:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Team Members</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List dense>
            {users.map((u) => (
              <ListItem key={u.id} disablePadding>
                <ListItemButton onClick={() => toggle(u.id)} dense>
                  <Checkbox
                    edge="start"
                    checked={selected.includes(u.id)}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                  <ListItemText
                    primary={u.name}
                    secondary={u.role}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={selected.length === 0 || saving}
        >
          {saving ? 'Adding…' : `Add ${selected.length > 0 ? `(${selected.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ProjectMessagesTab ────────────────────────────────────────────────────────

function ProjectMessagesTab({ projectId }) {
  const { user } = useAuth();
  const socket = useSocket();

  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChannel, setLoadingChannel] = useState(true);
  const [compose, setCompose] = useState('');
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [addMembersOpen, setAddMembersOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const composeRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Load project channel on mount
  useEffect(() => {
    let cancelled = false;
    setLoadingChannel(true);
    getProjectChannel(projectId)
      .then((res) => {
        if (cancelled) return;
        setChannel(res.data.channel);
        setMessages(res.data.messages);
      })
      .catch((err) => console.error('Failed to load project channel:', err))
      .finally(() => { if (!cancelled) setLoadingChannel(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  // Join socket room when channel is known
  useEffect(() => {
    if (!socket || !channel) return;
    socket.emit('join_channel', { channelId: channel.id });
  }, [socket, channel]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark as read when tab is active (messages loaded)
  useEffect(() => {
    if (!channel || messages.length === 0) return;
    markRead(channel.id).catch(() => {});
  }, [channel, messages.length]);

  // Real-time: new messages
  const handleNewMessage = useCallback((msg) => {
    if (!channel || msg.channel_id !== channel.id) return;
    setMessages((prev) => [...prev, msg]);
    markRead(channel.id).catch(() => {});
  }, [channel]);

  // Real-time: typing
  const handleUserTyping = useCallback(({ channelId, userName }) => {
    if (!channel || channelId !== channel.id) return;
    setTypingUsers((prev) => prev.includes(userName) ? prev : [...prev, userName]);
  }, [channel]);

  const handleUserStopTyping = useCallback(({ channelId, userName }) => {
    if (!channel || channelId !== channel.id) return;
    setTypingUsers((prev) => prev.filter((n) => n !== userName));
  }, [channel]);

  useEffect(() => {
    if (!socket) return;
    socket.on('new_message', handleNewMessage);
    socket.on('user_typing', handleUserTyping);
    socket.on('user_stop_typing', handleUserStopTyping);
    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('user_typing', handleUserTyping);
      socket.off('user_stop_typing', handleUserStopTyping);
    };
  }, [socket, handleNewMessage, handleUserTyping, handleUserStopTyping]);

  const handleSend = async () => {
    if (!compose.trim() || !channel || sending) return;
    const text = compose.trim();
    setCompose('');
    setSending(true);
    try {
      await sendMessage(channel.id, text);
    } catch (err) {
      setCompose(text); // restore on error
    } finally {
      setSending(false);
      composeRef.current?.focus();
    }
  };

  const handleComposeChange = (e) => {
    setCompose(e.target.value);
    if (!socket || !channel) return;
    socket.emit('typing', { channelId: channel.id });
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('stop_typing', { channelId: channel.id });
    }, 2000);
  };

  const handleComposeKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loadingChannel) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!channel) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Could not load project channel.</Alert>
      </Box>
    );
  }

  const currentUserId = user?.id;

  // Typing indicator text
  const typingOthers = typingUsers.filter((n) => n !== user?.name);
  const typingText = typingOthers.length === 1
    ? `${typingOthers[0]} is typing…`
    : typingOthers.length > 1
    ? `${typingOthers.join(', ')} are typing…`
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 500 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha('#1A5490', 0.04),
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatBubbleIcon sx={{ fontSize: 18, color: '#1A5490' }} />
          <Typography variant="subtitle2" fontWeight={700} color="#1A5490">
            {channel.name}
          </Typography>
        </Box>
        <Tooltip title="Add team members">
          <IconButton size="small" onClick={() => setAddMembersOpen(true)}>
            <PersonAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              gap: 1,
            }}
          >
            <ChatBubbleIcon sx={{ fontSize: 48, color: alpha('#1A5490', 0.2) }} />
            <Typography variant="body2" color="text.secondary">
              No messages yet — start the conversation!
            </Typography>
          </Box>
        ) : (
          messages.map((msg, idx) => (
            <PmMessageBubble
              key={msg.id}
              message={msg}
              prevMessage={idx > 0 ? messages[idx - 1] : null}
              currentUserId={currentUserId}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Typing indicator */}
      {typingText && (
        <Box sx={{ px: 2, pb: 0.25, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" fontStyle="italic">
            {typingText}
          </Typography>
        </Box>
      )}

      {/* Compose bar */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'flex-end',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            minHeight: 40,
          }}
        >
          <InputBase
            inputRef={composeRef}
            multiline
            maxRows={4}
            fullWidth
            placeholder="Message this project…"
            value={compose}
            onChange={handleComposeChange}
            onKeyDown={handleComposeKeyDown}
            sx={{ fontSize: '0.875rem' }}
          />
        </Paper>
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!compose.trim() || sending}
          sx={{
            bgcolor: compose.trim() ? '#1A5490' : undefined,
            color: compose.trim() ? '#fff' : undefined,
            '&:hover': { bgcolor: compose.trim() ? '#0d3560' : undefined },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            flexShrink: 0,
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Add members dialog */}
      <AddMembersDialog
        open={addMembersOpen}
        onClose={() => setAddMembersOpen(false)}
        channelId={channel.id}
        onMembersAdded={() => {}}
      />
    </Box>
  );
}

// ─── Installation Tasks Tab Component ─────────────────────────────────────────

const INST_STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: '#757575', bg: '#F5F5F5' },
  in_progress: { label: 'In Progress', color: '#1565C0', bg: '#E3F2FD' },
  completed:   { label: 'Completed',   color: '#2E7D32', bg: '#E8F5E9' },
  blocked:     { label: 'Blocked',     color: '#C62828', bg: '#FFEBEE' },
};

const INST_PHASE_COLORS = {
  2: '#4E342E',
  3: '#1565C0',
  4: '#E65100',
  5: '#2E7D32',
};

function InstStatusChip({ status }) {
  const cfg = INST_STATUS_CONFIG[status] || INST_STATUS_CONFIG.pending;
  return (
    <Box sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
      bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.72rem',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </Box>
  );
}

const InstallationTasksTab = ({
  project, instTasks, instTasksLoading, instTasksGenerating, instTasksMsg, setInstTasksMsg,
  instExpandedPhases, setInstExpandedPhases, instExpandedRooms, setInstExpandedRooms,
  instExpandedTask, setInstExpandedTask, instUsers, instBulkAssignPhase, setInstBulkAssignPhase,
  instBulkAssignee, setInstBulkAssignee,
  instBulkScheduledDate, setInstBulkScheduledDate,
  instPhaseGateDialog, setInstPhaseGateDialog,
  instCompletionDate, setInstCompletionDate,
  onGenerate, onTaskUpdate, onBulkAssign, isPmOrMgmt, onSaveCompletionDate,
  // Day Planner props (Tom Clancy 2026-05-26)
  instViewMode = 'planner', setInstViewMode = () => {},
  instSelectedTaskIds = new Set(), setInstSelectedTaskIds = () => {},
  instFilterDate = '', setInstFilterDate = () => {},
  instFilterAssignment = 'all', setInstFilterAssignment = () => {},
  instFilterAssignee = '', setInstFilterAssignee = () => {},
  instPlannerAssignee = '', setInstPlannerAssignee = () => {},
  instPlannerScheduledDate = '', setInstPlannerScheduledDate = () => {},
  onPlannerApply = () => {},
}) => {
  const hasCompletionDate = !!(project.projected_completion_date || project.sage_est_comp || instCompletionDate);
  const hasTasks = instTasks && instTasks.summary && instTasks.summary.total_tasks > 0;

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (instTasksLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
        <CircularProgress size={20} />
        <Typography color="text.secondary">Loading installation tasks…</Typography>
      </Box>
    );
  }

  // ── Not yet generated ───────────────────────────────────────────────────────
  if (!hasTasks) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 3 }}>
        <BuildIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary" textAlign="center">
          No installation tasks generated yet
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 480 }}>
          Review the proposal on the Proposals tab, then click to generate installation tasks from the sold items.
        </Typography>

        {!hasCompletionDate && isPmOrMgmt && (
          <Alert severity="warning" sx={{ maxWidth: 480, width: '100%' }}>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              Set a project completion date first
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                type="date"
                size="small"
                value={instCompletionDate}
                onChange={(e) => {
                  // Update local state AND fire the save immediately. Reading
                  // e.target.value here avoids the stale-closure problem we
                  // had with onBlur (instCompletionDate would still be the
                  // previous render's value when the handler runs).
                  // Tom Clancy 2026-04-30: 'I set the completion date and
                  // when I generate tasks I get "Set a project completion
                  // date before generating tasks".' — the save was never
                  // landing.
                  const v = e.target.value;
                  setInstCompletionDate(v);
                  if (v && onSaveCompletionDate) onSaveCompletionDate(v);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ bgcolor: 'white' }}
              />
              <Typography variant="caption" color="text.secondary">
                Saves automatically when you pick a date.
              </Typography>
            </Box>
          </Alert>
        )}

        {instTasksMsg && (
          <Alert severity={instTasksMsg.severity} onClose={() => setInstTasksMsg(null)} sx={{ maxWidth: 480, width: '100%' }}>
            {instTasksMsg.text}
          </Alert>
        )}

        {isPmOrMgmt && (
          <Button
            variant="contained"
            size="large"
            startIcon={instTasksGenerating ? <CircularProgress size={18} color="inherit" /> : <BuildIcon />}
            disabled={instTasksGenerating || !hasCompletionDate}
            onClick={onGenerate}
            sx={{ mt: 1 }}
          >
            {instTasksGenerating ? 'Generating Tasks…' : 'Generate Installation Tasks'}
          </Button>
        )}
      </Box>
    );
  }

  const { phases = [], summary = {} } = instTasks;

  const groupByRoom = (tasks) => {
    const rooms = {};
    const order = [];
    for (const t of tasks) {
      const room = t.room_name || 'System / General';
      if (!rooms[room]) { rooms[room] = []; order.push(room); }
      rooms[room].push(t);
    }
    return { rooms, order };
  };

  return (
    <Box>
      {instTasksMsg && (
        <Alert severity={instTasksMsg.severity} onClose={() => setInstTasksMsg(null)} sx={{ mb: 2 }}>
          {instTasksMsg.text}
        </Alert>
      )}

      {/* ── Phase Progress Bar ─────────────────────────────────────────────── */}
      {phases.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {phases.map((ph) => {
              const pct = ph.total > 0 ? (ph.completed / ph.total) * 100 : 0;
              const color = INST_PHASE_COLORS[ph.phase] || '#757575';
              return (
                <Box key={ph.phase} sx={{ flex: ph.total || 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.25, color, fontWeight: 'bold', fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ph.name}
                  </Typography>
                  <Box sx={{ position: 'relative', height: 12, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, bottom: 0,
                      width: `${pct}%`,
                      bgcolor: pct === 100 ? '#2E7D32' : pct > 0 ? '#1565C0' : 'grey.300',
                      transition: 'width 0.3s',
                    }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {ph.completed}/{ph.total}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Summary Cards ──────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { title: 'Total Tasks',  value: summary.total_tasks || 0,                              color: '#1565C0' },
          { title: 'Assigned',     value: summary.assigned || 0,                                 color: '#E65100' },
          { title: 'Completed',    value: summary.completed || 0,                                color: '#2E7D32' },
          { title: 'Unassigned',   value: (summary.total_tasks || 0) - (summary.assigned || 0),  color: '#757575' },
        ].map((card) => (
          <Grid item xs={6} sm={3} key={card.title}>
            <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{card.title}</Typography>
              <Typography variant="h4" fontWeight="bold" color={card.color}>{card.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── View mode toggle + filters (PM Day Planner) ─────────────────── */}
      {isPmOrMgmt && (() => {
        // Build flat task list for planner view + filter counts
        const allTasks = phases.flatMap((ph) =>
          ph.tasks.map((t) => ({ ...t, _phase: ph.phase, _phaseName: ph.name }))
        );
        const matchesFilters = (t) => {
          if (instFilterDate) {
            const sd = t.scheduled_date ? t.scheduled_date.substring(0, 10) : '';
            if (sd !== instFilterDate) return false;
          }
          if (instFilterAssignment === 'unassigned' && t.assignee_id) return false;
          if (instFilterAssignment === 'assigned' && !t.assignee_id) return false;
          if (instFilterAssignee && String(t.assignee_id || '') !== String(instFilterAssignee)) return false;
          return true;
        };
        const visibleTasks = allTasks.filter(matchesFilters);
        const visibleIds = new Set(visibleTasks.map((t) => t.id));
        const allVisibleSelected = visibleTasks.length > 0 && visibleTasks.every((t) => instSelectedTaskIds.has(t.id));
        const someVisibleSelected = visibleTasks.some((t) => instSelectedTaskIds.has(t.id));

        const toggleTask = (taskId) => {
          setInstSelectedTaskIds((prev) => {
            const next = new Set(prev);
            if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
            return next;
          });
        };
        const toggleAllVisible = () => {
          setInstSelectedTaskIds((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) {
              visibleIds.forEach((id) => next.delete(id));
            } else {
              visibleIds.forEach((id) => next.add(id));
            }
            return next;
          });
        };

        return (
          <Box sx={{ mb: 2 }}>
            {/* View mode toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                value={instViewMode}
                exclusive
                onChange={(_, v) => { if (v) setInstViewMode(v); }}
                size="small"
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}
              >
                <ToggleButton value="planner">Day Planner</ToggleButton>
                <ToggleButton value="phase">Phase / Room</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {instViewMode === 'planner'
                  ? 'Pick tasks, assign a tech and date, apply.'
                  : 'Tasks grouped by phase and room (legacy view).'}
              </Typography>
            </Box>

            {/* Filter bar (shown in both views; visual only) */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
              <TextField
                type="date"
                label="Scheduled on"
                size="small"
                value={instFilterDate}
                onChange={(e) => setInstFilterDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 170 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Assignment</InputLabel>
                <Select
                  value={instFilterAssignment}
                  label="Assignment"
                  onChange={(e) => setInstFilterAssignment(e.target.value)}
                >
                  <MenuItem value="all">All tasks</MenuItem>
                  <MenuItem value="unassigned">Unassigned only</MenuItem>
                  <MenuItem value="assigned">Assigned only</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Assignee</InputLabel>
                <Select
                  value={instFilterAssignee}
                  label="Assignee"
                  onChange={(e) => setInstFilterAssignee(e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {instUsers.filter((u) => u.active !== false).map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                Showing {visibleTasks.length} of {allTasks.length} tasks
                {instSelectedTaskIds.size > 0 && <> · <b>{instSelectedTaskIds.size} selected</b></>}
              </Typography>
              {(instFilterDate || instFilterAssignment !== 'all' || instFilterAssignee) && (
                <Button size="small" onClick={() => { setInstFilterDate(''); setInstFilterAssignment('all'); setInstFilterAssignee(''); }}>
                  Clear filters
                </Button>
              )}
            </Box>

            {/* Auto-filter notice for big projects (Tom Clancy 2026-06-09). */}
            {allTasks.length > 300 && instFilterAssignment === 'unassigned' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                This project has {allTasks.length} tasks. We're showing <b>Unassigned only</b> by default so the page loads quickly. Click <b>Clear filters</b> above to see everything.
              </Alert>
            )}

            {/* Day Planner: flat task list, grouped by project room */}
            {instViewMode === 'planner' && (
              <Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'grey.300' }}>
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={!allVisibleSelected && someVisibleSelected}
                    onChange={toggleAllVisible}
                    sx={{ p: 0.5 }}
                  />
                  <Typography variant="caption" fontWeight="bold">
                    Select all {visibleTasks.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    Task
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 3, mr: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130 }}>Assignee</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>Scheduled</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>Status</Typography>
                  </Box>
                </Box>
                {visibleTasks.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No tasks match the current filters.
                    </Typography>
                  </Box>
                ) : (
                  visibleTasks.map((task) => {
                    const phColor = INST_PHASE_COLORS[task._phase] || '#757575';
                    const selected = instSelectedTaskIds.has(task.id);
                    return (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          px: 2, py: 1, borderBottom: '1px solid', borderColor: 'grey.100',
                          bgcolor: selected ? 'primary.50' : task.status === 'completed' ? 'grey.50' : 'white',
                          borderLeft: `3px solid ${phColor}`,
                          '&:hover': { bgcolor: selected ? 'primary.100' : 'grey.50' },
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selected}
                          onChange={() => toggleTask(task.id)}
                          sx={{ p: 0.5 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                              color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
                              fontWeight: 500,
                            }}
                          >
                            {task.room_name && (
                              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                {task.room_name} ·{' '}
                              </Box>
                            )}
                            {task.task_name}
                            {task.bundle_count > 1 && (
                              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                {' '}(×{task.bundle_count})
                              </Box>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {task._phaseName}
                            {task.category && <> · {task.category}</>}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 130, display: { xs: 'none', sm: 'block' } }} onClick={(e) => e.stopPropagation()}>
                          <Select
                            size="small"
                            value={task.assignee_id || ''}
                            displayEmpty
                            fullWidth
                            onChange={(e) => onTaskUpdate(task.id, { assignee_id: e.target.value || null })}
                            sx={{ fontSize: '0.75rem' }}
                            renderValue={(v) => v
                              ? (instUsers.find((u) => u.id === v)?.name || 'Assigned')
                              : <em style={{ color: '#999' }}>Unassigned</em>
                            }
                          >
                            <MenuItem value=""><em>Unassigned</em></MenuItem>
                            {instUsers.filter((u) => u.active !== false).map((u) => (
                              <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <TextField
                          type="date"
                          size="small"
                          value={task.scheduled_date ? task.scheduled_date.substring(0, 10) : ''}
                          onChange={(e) => onTaskUpdate(task.id, { scheduled_date: e.target.value || null })}
                          slotProps={{ inputLabel: { shrink: true } }}
                          sx={{ minWidth: 130, display: { xs: 'none', sm: 'block' } }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Box sx={{ minWidth: 80, display: { xs: 'none', md: 'block' } }}>
                          <InstStatusChip status={task.status} />
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Paper>
            )}
          </Box>
        );
      })()}

      {/* ── Phases (legacy view) ───────────────────────────────────── */}
      {(!isPmOrMgmt || instViewMode === 'phase') && phases.map((ph) => {
        const phOpen = !!instExpandedPhases[ph.phase];
        const phColor = INST_PHASE_COLORS[ph.phase] || '#757575';
        const { rooms, order: roomOrder } = groupByRoom(ph.tasks);

        return (
          <Box key={ph.phase} sx={{ mb: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
            {/* Phase Header */}
            <Box
              onClick={() => setInstExpandedPhases((prev) => ({ ...prev, [ph.phase]: !prev[ph.phase] }))}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2, py: 1.5, bgcolor: phColor, color: 'white', cursor: 'pointer',
                '&:hover': { opacity: 0.92 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {phOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                <Typography variant="subtitle1" fontWeight="bold">{ph.name}</Typography>
                <Chip
                  label={`${ph.tasks.length} tasks`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {ph.completed}/{ph.total} done · {ph.assigned} assigned
                </Typography>
                {isPmOrMgmt && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', py: 0.25, px: 1 }}
                    onClick={(e) => { e.stopPropagation(); setInstBulkAssignPhase(ph.phase === instBulkAssignPhase ? null : ph.phase); }}
                  >
                    Bulk Assign
                  </Button>
                )}
              </Box>
            </Box>

            {/* Phase-level Bulk Assign Row */}
            {instBulkAssignPhase === ph.phase && isPmOrMgmt && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" fontWeight="medium">Assign / schedule all {ph.name} tasks:</Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Assignee</InputLabel>
                  <Select value={instBulkAssignee} label="Assignee" onChange={(e) => setInstBulkAssignee(e.target.value)}>
                    <MenuItem value=""><em>(no change)</em></MenuItem>
                    {instUsers.filter((u) => u.active !== false).map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  type="date"
                  label="Scheduled date"
                  size="small"
                  value={instBulkScheduledDate}
                  onChange={(e) => setInstBulkScheduledDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 170 }}
                />
                <Button variant="contained" size="small" disabled={!instBulkAssignee && !instBulkScheduledDate} onClick={() => onBulkAssign(ph.phase, null)}>Apply</Button>
                <Button size="small" onClick={() => { setInstBulkAssignPhase(null); setInstBulkAssignee(''); setInstBulkScheduledDate(''); }}>Cancel</Button>
              </Box>
            )}

            <Collapse in={phOpen}>
              {roomOrder.map((roomName) => {
                const roomTasks = rooms[roomName];
                const roomKey = `${ph.phase}-${roomName}`;
                const roomOpen = instExpandedRooms[roomKey] !== false;
                const roomCompleted = roomTasks.filter((t) => t.status === 'completed').length;

                return (
                  <Box key={roomName} sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    {/* Room Header */}
                    <Box
                      onClick={() => setInstExpandedRooms((prev) => ({ ...prev, [roomKey]: !roomOpen }))}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 3, py: 1, cursor: 'pointer',
                        bgcolor: roomOpen ? 'primary.50' : 'grey.50',
                        '&:hover': { bgcolor: 'primary.100' },
                        borderLeft: `3px solid ${phColor}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {roomOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <Typography variant="body2" fontWeight="bold">{roomName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({roomTasks.length} tasks, {roomCompleted} done)
                        </Typography>
                      </Box>
                      {isPmOrMgmt && (
                        <Button
                          size="small" variant="text" sx={{ fontSize: '0.7rem', py: 0.25 }}
                          onClick={(e) => { e.stopPropagation(); setInstBulkAssignPhase(`${ph.phase}-${roomName}`); }}
                        >
                          Assign Room
                        </Button>
                      )}
                    </Box>

                    {/* Room-level Bulk Assign Row */}
                    {instBulkAssignPhase === `${ph.phase}-${roomName}` && isPmOrMgmt && (
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', px: 3, py: 1, bgcolor: 'grey.50' }}>
                        <Typography variant="body2">Assign / schedule "{roomName}":</Typography>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Assignee</InputLabel>
                          <Select value={instBulkAssignee} label="Assignee" onChange={(e) => setInstBulkAssignee(e.target.value)}>
                            <MenuItem value=""><em>(no change)</em></MenuItem>
                            {instUsers.filter((u) => u.active !== false).map((u) => (
                              <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          type="date"
                          label="Scheduled date"
                          size="small"
                          value={instBulkScheduledDate}
                          onChange={(e) => setInstBulkScheduledDate(e.target.value)}
                          slotProps={{ inputLabel: { shrink: true } }}
                          sx={{ minWidth: 170 }}
                        />
                        <Button variant="contained" size="small" disabled={!instBulkAssignee && !instBulkScheduledDate} onClick={() => onBulkAssign(ph.phase, roomName)}>Apply</Button>
                        <Button size="small" onClick={() => { setInstBulkAssignPhase(null); setInstBulkAssignee(''); setInstBulkScheduledDate(''); }}>Cancel</Button>
                      </Box>
                    )}

                    <Collapse in={roomOpen}>
                      {roomTasks.map((task) => {
                        const isExpanded = instExpandedTask === task.id;
                        return (
                          <Box key={task.id} sx={{ borderBottom: '1px solid', borderColor: 'grey.50' }}>
                            {/* Task Row */}
                            <Box
                              onClick={() => setInstExpandedTask(isExpanded ? null : task.id)}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                px: 3, py: 1, cursor: 'pointer',
                                bgcolor: task.status === 'completed' ? 'grey.50' : 'white',
                                '&:hover': { bgcolor: 'grey.100' },
                              }}
                            >
                              {isPmOrMgmt ? (
                                <Checkbox
                                  checked={task.status === 'completed'}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onTaskUpdate(task.id, { status: task.status === 'completed' ? 'pending' : 'completed' });
                                  }}
                                  sx={{ p: 0.5, color: phColor, '&.Mui-checked': { color: '#2E7D32' } }}
                                />
                              ) : (
                                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
                                  {task.status === 'completed'
                                    ? <CheckCircleIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
                                    : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                  }
                                </Box>
                              )}

                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                                  color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
                                }}
                              >
                                {task.task_name}
                              </Typography>

                              {task.category && (
                                <Chip label={task.category} size="small" variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20, display: { xs: 'none', sm: 'flex' } }} />
                              )}

                              {isPmOrMgmt ? (
                                <FormControl size="small" sx={{ minWidth: 130, display: { xs: 'none', md: 'block' } }}
                                  onClick={(e) => e.stopPropagation()}>
                                  <Select
                                    value={task.assignee_id || ''}
                                    displayEmpty
                                    onChange={(e) => onTaskUpdate(task.id, { assignee_id: e.target.value || null })}
                                    sx={{ fontSize: '0.75rem' }}
                                    renderValue={(v) => v
                                      ? (instUsers.find((u) => u.id === v)?.name || 'Assigned')
                                      : <em style={{ color: '#999' }}>Unassigned</em>
                                    }
                                  >
                                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                                    {instUsers.filter((u) => u.active !== false).map((u) => (
                                      <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                task.assignee_name && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    {task.assignee_name}
                                  </Typography>
                                )
                              )}

                              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                <InstStatusChip status={task.status} />
                              </Box>

                              <ExpandMoreIcon
                                fontSize="small"
                                sx={{ color: 'text.disabled', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                              />
                            </Box>

                            {/* Expanded task detail */}
                            <Collapse in={isExpanded}>
                              <Box sx={{ px: 4, py: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.100' }}>
                                {task.item_description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    <strong>Item:</strong> {task.item_description}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mt: 1 }}>
                                  {isPmOrMgmt && (
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                      <InputLabel>Status</InputLabel>
                                      <Select value={task.status} label="Status"
                                        onChange={(e) => onTaskUpdate(task.id, { status: e.target.value })}>
                                        {Object.entries(INST_STATUS_CONFIG).map(([val, cfg]) => (
                                          <MenuItem key={val} value={val}>{cfg.label}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                  {isPmOrMgmt && (
                                    <TextField
                                      type="date" label="Scheduled" size="small"
                                      value={task.scheduled_date ? task.scheduled_date.substring(0, 10) : ''}
                                      onChange={(e) => onTaskUpdate(task.id, { scheduled_date: e.target.value || null })}
                                      slotProps={{ inputLabel: { shrink: true } }}
                                      sx={{ minWidth: 160 }}
                                      helperText="Day work happens"
                                    />
                                  )}
                                  {isPmOrMgmt && (
                                    <TextField
                                      type="date" label="Due" size="small"
                                      value={task.due_date ? task.due_date.substring(0, 10) : ''}
                                      onChange={(e) => onTaskUpdate(task.id, { due_date: e.target.value || null })}
                                      slotProps={{ inputLabel: { shrink: true } }}
                                      sx={{ minWidth: 160 }}
                                      helperText="Hard deadline"
                                    />
                                  )}
                                  {isPmOrMgmt && (
                                    <FormControl size="small" sx={{ minWidth: 160, display: { xs: 'block', md: 'none' } }}>
                                      <InputLabel>Assignee</InputLabel>
                                      <Select value={task.assignee_id || ''} label="Assignee"
                                        onChange={(e) => onTaskUpdate(task.id, { assignee_id: e.target.value || null })}>
                                        <MenuItem value=""><em>Unassigned</em></MenuItem>
                                        {instUsers.filter((u) => u.active !== false).map((u) => (
                                          <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                </Box>
                                {isPmOrMgmt && (
                                  <TextField
                                    label="Notes" multiline rows={2} size="small" fullWidth
                                    defaultValue={task.notes || ''}
                                    onBlur={(e) => {
                                      if (e.target.value !== (task.notes || '')) {
                                        onTaskUpdate(task.id, { notes: e.target.value });
                                      }
                                    }}
                                    sx={{ mt: 1.5 }}
                                    placeholder="Add notes…"
                                  />
                                )}
                                {!isPmOrMgmt && task.notes && (
                                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                    {task.notes}
                                  </Typography>
                                )}
                                {task.completed_at && (
                                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                                    Completed {fmtDate(task.completed_at)}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Collapse>
                  </Box>
                );
              })}
            </Collapse>
          </Box>
        );
      })}

      {/* Phase Gate Warning Dialog */}
      {instPhaseGateDialog && (
        <Dialog open onClose={() => setInstPhaseGateDialog(null)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" /> Phase Gate Warning
          </DialogTitle>
          <DialogContent>
            <Typography>{instPhaseGateDialog.warning}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You can override and mark this task complete anyway.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInstPhaseGateDialog(null)}>Cancel</Button>
            <Button variant="contained" color="warning"
              onClick={() => {
                const { taskId, data } = instPhaseGateDialog;
                setInstPhaseGateDialog(null);
                onTaskUpdate(taskId, data, true);
              }}>
              Override &amp; Complete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Sticky Action Bar (Day Planner) ────────────────────────── */}
      {isPmOrMgmt && instSelectedTaskIds.size > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: { xs: 0, sm: 240 }, // account for sidebar on desktop
            right: 0,
            zIndex: 1300,
            bgcolor: 'background.paper',
            borderTop: '2px solid',
            borderColor: 'primary.main',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {instSelectedTaskIds.size} task{instSelectedTaskIds.size === 1 ? '' : 's'} selected
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Assign to</InputLabel>
            <Select
              value={instPlannerAssignee}
              label="Assign to"
              onChange={(e) => setInstPlannerAssignee(e.target.value)}
            >
              <MenuItem value=""><em>(no change)</em></MenuItem>
              {instUsers.filter((u) => u.active !== false).map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="date"
            label="Scheduled date"
            size="small"
            value={instPlannerScheduledDate}
            onChange={(e) => setInstPlannerScheduledDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ minWidth: 170 }}
          />
          <Button
            variant="contained"
            disabled={!instPlannerAssignee && !instPlannerScheduledDate}
            onClick={onPlannerApply}
          >
            Apply
          </Button>
          <Button
            variant="text"
            onClick={() => { setInstSelectedTaskIds(new Set()); setInstPlannerAssignee(''); setInstPlannerScheduledDate(''); }}
            sx={{ ml: 'auto' }}
          >
            Clear selection
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ─── Main ProjectDetail component ─────────────────────────────────────────────

// Tab indices. The Proposal tab is shown whenever the user can VIEW proposals
// (canViewProposal — broader than canEditProject; CAD/Engineering/etc. see it
// too). The Financial tab only shows when canEditProject AND profitability
// access. Tom Clancy 2026-05-14.
// With Proposal:    Proposal(0) Contacts(1) Activity(2) FieldNotes(3) Assignments(4) InstallTasks(5) Timeline(6) RackBldr(7) Bin(8) POs(9) UnbillableLabor(10) Wire(11) Financial(12)
// Without Proposal: Contacts(0) Activity(1) ...
function getTabIndex(tabName, canViewProposal) {
  const offset = canViewProposal ? 1 : 0;
  const map = {
    proposal:         0, // mgmt only, no offset
    contacts:         0 + offset,
    activity:         1 + offset,
    field_notes:      2 + offset,
    assignments:      3 + offset,
    installation:     4 + offset,
    timeline:         5 + offset,
    rack_builder:     6 + offset,
    bin:              7 + offset,
    purchase_orders:  8 + offset,
    unbillable_labor: 9 + offset,
    messages:        10 + offset,
    financial:       11 + offset, // mgmt only
  };
  return map[tabName] ?? 0;
}

// ─── Meeting Notes Section (rendered in Activity tab) ─────────────────────────

const MeetingNotesSection = ({ projectId }) => {
  const [meetings, setMeetings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState({});
  const [importingId, setImportingId] = React.useState(null);
  const [importMsg, setImportMsg] = React.useState(null);

  React.useEffect(() => {
    setLoading(true);
    getProjectMeetings(projectId)
      .then(res => setMeetings(res.data || []))
      .catch(() => setMeetings([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleImportActionItems = async (meeting) => {
    const items = Array.isArray(meeting.action_items)
      ? meeting.action_items
      : (typeof meeting.action_items === 'string' ? JSON.parse(meeting.action_items) : []);
    if (!items.length) return;
    setImportingId(meeting.id);
    try {
      for (const item of items) {
        const text = typeof item === 'string' ? item : (item.text || '');
        if (text) await createWorkItem(projectId, { title: text, status: 'open', source: 'meeting_notes' });
      }
      setImportMsg(`Imported ${items.length} action item${items.length !== 1 ? 's' : ''} as Work Items`);
      setTimeout(() => setImportMsg(null), 3000);
    } catch (err) {
      console.error('Import action items error:', err);
    } finally {
      setImportingId(null);
    }
  };

  const sourceLabel = (src) => {
    const map = { read_ai: 'Read.ai', fireflies: 'Fireflies', otter: 'Otter.ai', manual: 'Manual', unknown: 'Other' };
    return map[src] || src;
  };

  const fmtMeetingDate = (d) => {
    if (!d) return 'No date';
    try { return format(new Date(d), 'MMM d, yyyy'); } catch { return d; }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, mb: 2 }}>
      <CircularProgress size={16} />
      <Typography variant="body2" color="text.secondary">Loading meeting notes…</Typography>
    </Box>
  );

  if (meetings.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CalendarTodayIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight="bold">
          Meeting Notes ({meetings.length})
        </Typography>
      </Box>
      {importMsg && (
        <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="body2">{importMsg}</Typography>
        </Alert>
      )}
      {meetings.map(m => {
        const isOpen = !!expanded[m.id];
        const actionItems = Array.isArray(m.action_items)
          ? m.action_items
          : (typeof m.action_items === 'string' ? (() => { try { return JSON.parse(m.action_items); } catch { return []; } })() : []);
        const participantCount = Array.isArray(m.participants) ? m.participants.length : 0;
        return (
          <Paper
            key={m.id}
            variant="outlined"
            sx={{ mb: 1.5, overflow: 'hidden', borderColor: 'divider' }}
          >
            {/* Header row */}
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25,
                cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => toggleExpand(m.id)}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: 280 }}>
                    {m.meeting_title || 'Untitled Meeting'}
                  </Typography>
                  <Chip label={sourceLabel(m.source)} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {fmtMeetingDate(m.meeting_date)}
                  {m.duration_minutes ? ` · ${m.duration_minutes} min` : ''}
                  {participantCount > 0 ? ` · ${participantCount} participant${participantCount !== 1 ? 's' : ''}` : ''}
                  {m.user_name ? ` · ${m.user_name}` : ''}
                </Typography>
              </Box>
              <IconButton size="small">
                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            {/* Expanded content */}
            <Collapse in={isOpen}>
              <Divider />
              <Box sx={{ p: 2 }}>
                {m.summary && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Summary
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                      {m.summary}
                    </Typography>
                  </Box>
                )}

                {actionItems.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Action Items ({actionItems.length})
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={importingId === m.id}
                        onClick={(e) => { e.stopPropagation(); handleImportActionItems(m); }}
                        startIcon={importingId === m.id ? <CircularProgress size={12} /> : null}
                        sx={{ py: 0, fontSize: '0.7rem', height: 24 }}
                      >
                        Import as Work Items
                      </Button>
                    </Box>
                    <List dense disablePadding>
                      {actionItems.map((item, idx) => {
                        const text = typeof item === 'string' ? item : (item.text || '');
                        const assignee = typeof item === 'object' ? item.assignee : null;
                        return (
                          <ListItem key={idx} disableGutters sx={{ py: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                              <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.1, flexShrink: 0 }} />
                              <Typography variant="body2">
                                {text}
                                {assignee && <Typography component="span" variant="caption" color="text.secondary"> — {assignee}</Typography>}
                              </Typography>
                            </Box>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}

                {Array.isArray(m.key_topics) && m.key_topics.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      Key Topics
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {m.key_topics.map((t, i) => (
                        <Chip key={i} label={t} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                      ))}
                    </Box>
                  </Box>
                )}

                {m.transcript && (
                  <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Full Transcript
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Box sx={{ maxHeight: 300, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                        {m.transcript}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
      <Divider sx={{ mt: 1, mb: 2 }} />
    </Box>
  );
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const isPmOrMgmt = user?.role === 'PM' || user?.role === 'Management' || user?.role === 'Field Operations' ||
    (Array.isArray(user?.roles) && (user.roles.includes('PM') || user.roles.includes('Management') || user.roles.includes('Field Operations')));
  const _canEditProjectByRole = isPmOrMgmt || user?.role === 'Sales' ||
    (Array.isArray(user?.roles) && user.roles.includes('Sales'));
  // canViewProposal: broader than canEditProject. CAD, Engineering, Production,
  // Shade managers, etc. all need to see proposals (sell prices, scope, items)
  // even though they don't edit projects. Profitability redaction happens
  // server-side based on user.profitabilityAccess. Tom Clancy 2026-05-14.
  const canViewProposal = !!user;
  // profitabilityAccess from JWT. When false, cost/margin/profit columns are
  // hidden in the Proposal tab and the entire Sage/Financial sections.
  const hasProfitability = user?.profitabilityAccess === true;

  const pdPrefs = preferences.projectDetail || {};
  const hiddenSections = useMemo(() => pdPrefs.hiddenSections || [], [pdPrefs.hiddenSections]);
  const isSectionHidden = (key) => hiddenSections.includes(key);

  // Allow deep-linking via ?tab=bin or ?tab=timeline etc.
  const urlTab = new URLSearchParams(routerLocation.search).get('tab');
  const resolveTab = (name) => name ? getTabIndex(name, canViewProposal) : undefined;
  // Default to Activity tab. Index depends on whether Proposal is shown.
  const defaultTabIndex = resolveTab(urlTab) ?? resolveTab(pdPrefs.defaultTab) ?? (canViewProposal ? 2 : 1);

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);

  // ── Archived / Closed Job read-only mode (Tom 2026-05-20) ──────────────────
  // Derived here (after project state declaration) so the values are in
  // scope for every useEffect / dep array below. When `project` is still
  // null (initial load), isArchived is false and canEditProject collapses
  // to the role-only check — effectively no-op until project loads.
  //
  // A project is considered archived when status='completed' or bucket is
  // in ('archive','archive_dupe','completed'). When archived:
  //   - canEditProject is false (cascades through every `canEditProject &&`
  //     gate — PM/Sales edit icons, work item adds, etc.)
  //   - Advance Phase button is hidden (`canAdvance` falls through)
  //   - ARCHIVED banner renders below the back button
  // Management retains a single carve-out: they can still edit *Status*
  // (`canEditStatus`) so they can un-archive a project from this page.
  const isMgmt = user?.role === 'Management'
    || (Array.isArray(user?.roles) && user.roles.includes('Management'));
  const isArchived = !!project && (
    project.status === 'completed'
    || ['archive', 'archive_dupe', 'completed'].includes(project.bucket || '')
  );
  const canEditProject = _canEditProjectByRole && !isArchived;
  // Status field gets a separate gate — Management can flip a completed
  // project back to active even when canEditProject is locked.
  const canEditStatus = _canEditProjectByRole && (!isArchived || isMgmt);

  const [activeTab, setActiveTab] = useState(defaultTabIndex);
  const sandboxRef = useRef(null);
  const [noteFilter, setNoteFilter] = useState('all');
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [laborEntries, setLaborEntries] = useState([]);
  const [laborExpanded, setLaborExpanded] = useState(false);
  const [laborLoading, setLaborLoading] = useState(false);
  const [generatingRaci, setGeneratingRaci] = useState(false);
  const [raciGenMsg, setRaciGenMsg] = useState(null);
  const [laborSortBy, setLaborSortBy] = useState('');
  const [laborSortDir, setLaborSortDir] = useState('asc');
  const [dealsSortBy, setDealsSortBy] = useState('');
  const [dealsSortDir, setDealsSortDir] = useState('asc');
  const [messagesUnread, setMessagesUnread] = useState(0);

  // D-Tools proposal state
  const [proposal, setProposal] = useState(null);
  const [proposalItemsByLocation, setProposalItemsByLocation] = useState([]);
  const [proposalCategorySummary, setProposalCategorySummary] = useState([]);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposalLoaded, setProposalLoaded] = useState(false);
  const [proposalSyncing, setProposalSyncing] = useState(false);
  const [proposalSyncMsg, setProposalSyncMsg] = useState(null);
  const [proposalError, setProposalError] = useState(null);

  // Proposal regeneration state (PM-triggered after CO approvals)
  const [proposalRegen, setProposalRegen] = useState(null); // { stale, last_regeneration, pending_change_orders, pending_co_count }

  // Pending change orders from D-Tools (unapproved revised proposals). Surfaced
  // in the Project Sandbox subheader so PMs see what's awaiting client sign-off.
  // Tom Clancy 2026-05-14.
  const [pendingCOs, setPendingCOs] = useState({ count: 0, aged_count: 0, pending: [] });
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [regenNote, setRegenNote] = useState('');
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  const [regenError, setRegenError] = useState(null);
  const [regenSuccessMsg, setRegenSuccessMsg] = useState(null);
  const [regenHistory, setRegenHistory] = useState(null);
  const [regenHistoryOpen, setRegenHistoryOpen] = useState(false);
  const canRegenerate = user?.role === 'PM' || user?.role === 'Management' ||
    (Array.isArray(user?.roles) && (user.roles.includes('PM') || user.roles.includes('Management')));
  const [scopeExpanded, setScopeExpanded] = useState(false);
  const [expandedRooms, setExpandedRooms] = useState({});

  // Installation Tasks state
  const [instTasks, setInstTasks] = useState(null);         // null = not loaded
  const [instTasksLoading, setInstTasksLoading] = useState(false);
  const [instTasksGenerating, setInstTasksGenerating] = useState(false);
  const [instTasksMsg, setInstTasksMsg] = useState(null);   // { severity, text }
  const [instExpandedPhases, setInstExpandedPhases] = useState({});
  const [instExpandedRooms, setInstExpandedRooms] = useState({});
  const [instExpandedTask, setInstExpandedTask] = useState(null);
  const [instUsers, setInstUsers] = useState([]);
  const [instBulkAssignPhase, setInstBulkAssignPhase] = useState(null);
  const [instBulkAssignee, setInstBulkAssignee] = useState('');
  const [instBulkScheduledDate, setInstBulkScheduledDate] = useState('');
  const [instPhaseGateDialog, setInstPhaseGateDialog] = useState(null); // { taskId, warning, data }
  const [instCompletionDate, setInstCompletionDate] = useState('');

  // ── Day Planner state (PM workflow: select tasks across rooms, assign in batch)
  // Tom Clancy 2026-05-26: PMs need a multi-select + apply flow, not just
  // phase/room bulk-assign. The day planner view is the default.
  const [instViewMode, setInstViewMode] = useState('planner'); // 'planner' | 'phase'
  const [instSelectedTaskIds, setInstSelectedTaskIds] = useState(() => new Set());
  const [instFilterDate, setInstFilterDate] = useState('');
  const [instFilterAssignment, setInstFilterAssignment] = useState('all'); // 'all' | 'unassigned' | 'assigned'
  const [instFilterAssignee, setInstFilterAssignee] = useState('');
  const [instPlannerAssignee, setInstPlannerAssignee] = useState('');
  const [instPlannerScheduledDate, setInstPlannerScheduledDate] = useState('');

  // Timeline review mandate banner
  const [timelineNeedsReview, setTimelineNeedsReview] = useState(false);
  const [timelineDaysSince, setTimelineDaysSince] = useState(null);
  const [timelineReason, setTimelineReason] = useState('ok');
  const [timelineMissingPhases, setTimelineMissingPhases] = useState([]);
  const [timelineBannerDismissed, setTimelineBannerDismissed] = useState(false);

  // PM / Sales inline editing (Management only)
  const [editingPM, setEditingPM] = useState(false);
  const [editingSales, setEditingSales] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedPM, setSelectedPM] = useState(null);
  const [selectedSales, setSelectedSales] = useState(null);
  const [assignSnackOpen, setAssignSnackOpen] = useState(false);
  const [assignSnackMsg, setAssignSnackMsg] = useState('');
  const [taskSnackOpen, setTaskSnackOpen] = useState(false);
  const [taskSnackMsg, setTaskSnackMsg] = useState('');

  // Status inline editing
  const [editingStatus, setEditingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');


  useEffect(() => {
    loadProject();
  }, [id]);

  // Pre-fetch user list for PM/Sales assignment (Management, PM, Sales)
  useEffect(() => {
    if (!canEditProject) return;
    getUsers()
      .then((res) => setAllUsers(res.data || []))
      .catch(() => {});
  }, [canEditProject]);

  // Load unread count for the Messages tab badge
  useEffect(() => {
    if (!id) return;
    getChannels()
      .then((res) => {
        const channels = res.data || [];
        const projectCh = channels.find(
          (c) => c.type === 'project' && String(c.project_id) === String(id)
        );
        if (projectCh) {
          setMessagesUnread(parseInt(projectCh.unread_count) || 0);
        }
      })
      .catch(() => {});
  }, [id]);

  // Clear unread badge when switching to Messages tab
  useEffect(() => {
    if (activeTab === getTabIndex('messages', canViewProposal) && messagesUnread > 0) {
      setMessagesUnread(0);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProject = async () => {
    const savedY = window.scrollY;
    setLoading(true);
    try {
      const response = await getProject(id);
      setProject(response.data);
      requestAnimationFrame(() => window.scrollTo(0, savedY));
    } catch (error) {
      console.error('Failed to load project:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkTimelineStatus = async () => {
    if (!isPmOrMgmt) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/projects/${id}/timeline-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { needs_review, reason, missing_phases, days_since_review } = res.data;
      setTimelineNeedsReview(needs_review);
      setTimelineDaysSince(days_since_review);
      setTimelineReason(reason || 'ok');
      setTimelineMissingPhases(missing_phases || []);
    } catch (err) {
      console.error('Timeline status check failed:', err);
    }
  };

  useEffect(() => {
    if (id) checkTimelineStatus();
  }, [id, isPmOrMgmt]);

  // Prepend a newly-saved voice note to the local activity list without a full reload
  const handleVoiceNoteSaved = (note) => {
    setProject(prev => prev ? { ...prev, activity: [note, ...(prev.activity || [])] } : prev);
  };

  const loadLaborEntries = async () => {
    if (laborLoading) return;
    setLaborLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/labor/project/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLaborEntries(response.data || []);
    } catch (err) {
      console.error('Failed to load labor entries:', err);
      setLaborEntries([]);
    } finally {
      setLaborLoading(false);
    }
  };

  const handleLaborToggle = () => {
    if (!laborExpanded && laborEntries.length === 0) {
      loadLaborEntries();
    }
    setLaborExpanded((prev) => !prev);
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    // Optimistic update — flip the task status in local state immediately
    setProject((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map((t) =>
          t.id === taskId ? { ...t, status: newStatus } : t
        ),
      };
    });
    try {
      await updateTaskStatus(taskId, newStatus);
      setTaskSnackMsg(newStatus === 'completed' ? '✓ Task completed' : 'Task marked pending');
      setTaskSnackOpen(true);
      // Lightweight background refresh (keeps counts/approvals accurate) without blocking
      getProject(id).then((res) => {
        const savedY = window.scrollY;
        setProject(res.data);
        requestAnimationFrame(() => window.scrollTo(0, savedY));
      }).catch(() => {});
    } catch (error) {
      console.error('Failed to update task:', error);
      // Revert optimistic update on failure
      await loadProject();
      setTaskSnackMsg('Failed to update task — reverted');
      setTaskSnackOpen(true);
    }
  };

  const handleTaskApprove = async (taskId) => {
    // Optimistic update — mark approved immediately
    setProject((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map((t) =>
          t.id === taskId ? { ...t, approved_by: user?.id || true } : t
        ),
      };
    });
    try {
      await approveTask(taskId);
      setTaskSnackMsg('✓ Task approved');
      setTaskSnackOpen(true);
      getProject(id).then((res) => {
        const savedY = window.scrollY;
        setProject(res.data);
        requestAnimationFrame(() => window.scrollTo(0, savedY));
      }).catch(() => {});
    } catch (error) {
      console.error('Failed to approve task:', error);
      await loadProject();
      setTaskSnackMsg('Failed to approve task — reverted');
      setTaskSnackOpen(true);
    }
  };

  const handleRaciTaskAssign = async (taskId, userId) => {
    try {
      await assignRaciTask(id, taskId, { user_id: userId, raci_type: 'R' });
      await loadProject();
    } catch (err) {
      console.error('Failed to assign task:', err);
    }
  };

  const handleAdvancePhase = async () => {
    try {
      await advancePhase(id);
      setAdvanceDialogOpen(false);
      await loadProject();
    } catch (error) {
      console.error('Failed to advance phase:', error);
      alert(error.response?.data?.error || 'Failed to advance phase');
    }
  };

  const handleGenerateRaciTasks = async () => {
    setGeneratingRaci(true);
    setRaciGenMsg(null);
    try {
      const res = await generateRaciTasks(id, project.current_phase);
      const count = res.data.tasks_created;
      const msg = count > 0
        ? `Generated ${count} RACI task${count !== 1 ? 's' : ''} for this phase.`
        : 'All RACI tasks already exist for this phase.';
      setRaciGenMsg(msg);
      setTaskSnackMsg(msg);
      setTaskSnackOpen(true);
      await loadProject();
    } catch (error) {
      console.error('Failed to generate RACI tasks:', error);
      setRaciGenMsg('Failed to generate RACI tasks.');
    } finally {
      setGeneratingRaci(false);
    }
  };

  const handleLaborSort = (col) => {
    if (laborSortBy === col) {
      if (laborSortDir === 'asc') setLaborSortDir('desc');
      else { setLaborSortBy(''); setLaborSortDir('asc'); }
    } else {
      setLaborSortBy(col);
      setLaborSortDir('asc');
    }
  };

  const sortLaborEntries = (data) => {
    if (!laborSortBy) return data;
    return [...data].sort((a, b) => {
      let aVal = a[laborSortBy] ?? '';
      let bVal = b[laborSortBy] ?? '';
      if (typeof aVal === 'number' || (!isNaN(parseFloat(aVal)) && aVal !== '')) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return laborSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return laborSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleDealsSort = (col) => {
    if (dealsSortBy === col) {
      if (dealsSortDir === 'asc') setDealsSortDir('desc');
      else { setDealsSortBy(''); setDealsSortDir('asc'); }
    } else {
      setDealsSortBy(col);
      setDealsSortDir('asc');
    }
  };

  const sortDeals = (data) => {
    if (!dealsSortBy) return data;
    return [...data].sort((a, b) => {
      let aVal = a[dealsSortBy] ?? '';
      let bVal = b[dealsSortBy] ?? '';
      if (typeof aVal === 'number' || (!isNaN(parseFloat(aVal)) && aVal !== '')) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return dealsSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return dealsSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Load pending change orders for the sandbox header. Lightweight, fires on
  // project load. Tom Clancy 2026-05-14.
  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios.get(`/api/projects/${project.id}/pending-change-orders`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!cancelled) setPendingCOs(r.data || { count: 0, aged_count: 0, pending: [] }); })
      .catch((err) => { if (!cancelled) { console.error('Failed to load pending COs:', err); } });
    return () => { cancelled = true; };
  }, [project?.id]);

  // Load D-Tools proposal data lazily when tab becomes active. Allowed for
  // any user with canViewProposal; profitability redaction happens server-side.
  useEffect(() => {
    if (activeTab !== getTabIndex('proposal', canViewProposal) || !canViewProposal || proposalLoaded || proposalLoading) return;
    setProposalLoading(true);
    setProposalError(null);
    getProjectProposal(id)
      .then((res) => {
        const data = res.data || {};
        setProposal(data.proposal || null);
        setProposalItemsByLocation(data.items_by_location || []);
        setProposalCategorySummary(data.category_summary || []);
        setProposalRegen(data.regeneration || null);
        // Default: open first room
        if (data.items_by_location && data.items_by_location.length > 0) {
          setExpandedRooms({ [data.items_by_location[0].location_name]: true });
        }
        setProposalLoaded(true);
      })
      .catch((err) => {
        console.error('Failed to load proposal:', err);
        setProposalError('Failed to load proposal data.');
      })
      .finally(() => setProposalLoading(false));
  }, [activeTab, canViewProposal, id, proposalLoaded, proposalLoading]);

  const toggleRoom = (locationName) => {
    setExpandedRooms((prev) => ({ ...prev, [locationName]: !prev[locationName] }));
  };

  // ─── Proposal Regeneration handlers ───────────────────────────────────
  const handleRegenerateProposal = async () => {
    setRegenSubmitting(true);
    setRegenError(null);
    try {
      const res = await regenerateProposal(id, regenNote.trim() || null);
      const incorporated = res.data?.incorporated_co_count || 0;
      const sync = res.data?.sync_status;
      let msg = `Proposal regenerated — ${incorporated} change order${incorporated === 1 ? '' : 's'} incorporated.`;
      if (sync && sync !== 'success') msg += ` (Sync status: ${sync})`;
      setRegenSuccessMsg(msg);
      setRegenDialogOpen(false);
      setRegenNote('');
      // Reload proposal so the stale banner / labor totals refresh
      const refreshed = await getProjectProposal(id);
      const data = refreshed.data || {};
      setProposal(data.proposal || null);
      setProposalItemsByLocation(data.items_by_location || []);
      setProposalCategorySummary(data.category_summary || []);
      setProposalRegen(data.regeneration || null);
      // Reload history if open
      if (regenHistoryOpen) {
        const h = await getProposalRegenerationHistory(id);
        setRegenHistory(h.data?.regenerations || []);
      }
      setTimeout(() => setRegenSuccessMsg(null), 6000);
    } catch (err) {
      console.error('Regenerate proposal error:', err);
      setRegenError(err.response?.data?.error || 'Regeneration failed.');
    } finally {
      setRegenSubmitting(false);
    }
  };

  const openRegenHistory = async () => {
    setRegenHistoryOpen(true);
    if (regenHistory === null) {
      try {
        const h = await getProposalRegenerationHistory(id);
        setRegenHistory(h.data?.regenerations || []);
      } catch (err) {
        console.error('Load regen history error:', err);
        setRegenHistory([]);
      }
    }
  };

  // Load installation tasks lazily when tab becomes active (tab index 9)
  useEffect(() => {
    if (activeTab !== getTabIndex('installation', canViewProposal)) return;
    if (instTasks !== null || instTasksLoading) return;
    setInstTasksLoading(true);
    getInstallationTasks(id)
      .then((res) => {
        setInstTasks(res.data);
        // Default: expand all phases
        const phases = res.data.phases || [];
        const expanded = {};
        phases.forEach((p) => { expanded[p.phase] = true; });
        setInstExpandedPhases(expanded);
        // Big-project guard: if a project has more than 300 tasks, default the
        // Day Planner to "Unassigned only" so we don't render thousands of rows
        // on first load (would freeze phones / hang desktop). PM can clear the
        // filter to see everything. Tom Clancy 2026-06-09.
        const totalTasks = res.data?.summary?.total_tasks || 0;
        if (totalTasks > 300) {
          setInstFilterAssignment((prev) => (prev === 'all' ? 'unassigned' : prev));
        }
      })
      .catch(() => {
        setInstTasks({ phases: [], summary: { total_tasks: 0, assigned: 0, completed: 0, by_category: {} } });
      })
      .finally(() => setInstTasksLoading(false));
  }, [activeTab, id, instTasks, instTasksLoading]);

  // Load users for assignee dropdown when tab active
  useEffect(() => {
    if (activeTab !== getTabIndex('installation', canViewProposal) || instUsers.length > 0) return;
    getUsers().then((res) => setInstUsers(res.data || [])).catch(() => {});
  }, [activeTab, instUsers.length]);

  const handleGenerateInstallationTasks = async () => {
    setInstTasksGenerating(true);
    setInstTasksMsg(null);
    try {
      // Pass instCompletionDate as a fallback so the server can persist it
      // inline if our prior save didn't land (race or user clicked Generate
      // before onChange-save resolved).
      const fallbackDate = (
        instCompletionDate ||
        (project?.projected_completion_date ? String(project.projected_completion_date).slice(0, 10) : null)
      );
      const res = await generateInstallationTasks(id, fallbackDate);
      const { tasks_created, by_phase } = res.data;
      const phaseSummary = Object.entries(by_phase)
        .map(([p, n]) => `${p}: ${n}`)
        .join(', ');
      setInstTasksMsg({ severity: 'success', text: `Generated ${tasks_created} tasks — ${phaseSummary}` });
      // Reload tasks
      setInstTasks(null);
      const reload = await getInstallationTasks(id);
      setInstTasks(reload.data);
      const phases = reload.data.phases || [];
      const expanded = {};
      phases.forEach((p) => { expanded[p.phase] = true; });
      setInstExpandedPhases(expanded);
    } catch (err) {
      setInstTasksMsg({ severity: 'error', text: err.response?.data?.error || 'Failed to generate tasks' });
    } finally {
      setInstTasksGenerating(false);
    }
  };

  const handleSaveCompletionDate = async (dateStr) => {
    try {
      await updateProject(id, { projected_completion_date: dateStr });
      // Refresh project so hasCompletionDate goes true and unlock the generate button
      await loadProject();
    } catch (err) {
      console.error('Failed to save completion date:', err);
      setInstTasksMsg({ severity: 'error', text: 'Failed to save completion date. Please try again.' });
    }
  };

  const handleInstTaskUpdate = async (taskId, data, overrideGate = false) => {
    // Optimistic update — apply the field changes immediately in local state
    const savedTasks = instTasks;
    setInstTasks((prev) => {
      if (!prev) return prev;
      const updatedPhases = prev.phases.map((ph) => ({
        ...ph,
        tasks: ph.tasks.map((t) => (t.id === taskId ? { ...t, ...data } : t)),
      }));
      // Recompute summary counts
      const allTasks = updatedPhases.flatMap((ph) => ph.tasks);
      const completed = allTasks.filter((t) => t.status === 'completed').length;
      const assigned = allTasks.filter((t) => t.assignee_id).length;
      return {
        ...prev,
        phases: updatedPhases,
        summary: { ...prev.summary, completed, assigned },
      };
    });
    try {
      const payload = overrideGate ? { ...data, override_phase_gate: true } : data;
      await updateInstallationTask(id, taskId, payload);
      // Background sync to pick up any server-side changes (timestamps, phase counts)
      getInstallationTasks(id).then((reload) => {
        setInstTasks(reload.data);
      }).catch(() => {});
    } catch (err) {
      // Revert on failure
      setInstTasks(savedTasks);
      if (err.response?.status === 409 && err.response?.data?.phase_gate) {
        setInstPhaseGateDialog({ taskId, warning: err.response.data.error, data });
      } else {
        setInstTasksMsg({ severity: 'error', text: err.response?.data?.error || 'Failed to update task' });
      }
    }
  };

  // ── Day Planner: bulk-assign a set of arbitrary task IDs.
  // Uses the existing /installation-tasks/bulk-assign endpoint with task_ids.
  const handlePlannerApply = async () => {
    const ids = Array.from(instSelectedTaskIds);
    if (ids.length === 0) return;
    if (!instPlannerAssignee && !instPlannerScheduledDate) return;

    const assigneeId = instPlannerAssignee ? parseInt(instPlannerAssignee) : null;
    const assigneeName = assigneeId
      ? instUsers.find((u) => u.id === assigneeId)?.name || ''
      : null;
    const scheduledDate = instPlannerScheduledDate || null;

    const savedTasks = instTasks;
    // Optimistic update across all phases
    setInstTasks((prev) => {
      if (!prev) return prev;
      const idSet = new Set(ids);
      const updatedPhases = prev.phases.map((ph) => ({
        ...ph,
        tasks: ph.tasks.map((t) => {
          if (!idSet.has(t.id)) return t;
          const next = { ...t };
          if (assigneeId !== null) {
            next.assignee_id = assigneeId;
            next.assignee_name = assigneeName;
          }
          if (scheduledDate) next.scheduled_date = scheduledDate;
          return next;
        }),
      }));
      const allTasks = updatedPhases.flatMap((ph) => ph.tasks);
      const assigned = allTasks.filter((t) => t.assignee_id).length;
      return { ...prev, phases: updatedPhases, summary: { ...prev.summary, assigned } };
    });

    try {
      const payload = { task_ids: ids };
      if (assigneeId !== null) payload.assignee_id = assigneeId;
      if (scheduledDate) payload.scheduled_date = scheduledDate;
      await bulkAssignTasks(id, payload);
      // Clear selection + inputs, show toast
      setInstSelectedTaskIds(new Set());
      setInstPlannerAssignee('');
      setInstPlannerScheduledDate('');
      const target = scheduledDate ? ` for ${scheduledDate}` : '';
      const who = assigneeName ? ` to ${assigneeName}` : '';
      setInstTasksMsg({ severity: 'success', text: `Assigned ${ids.length} task${ids.length === 1 ? '' : 's'}${who}${target}.` });
      getInstallationTasks(id).then((reload) => setInstTasks(reload.data)).catch(() => {});
    } catch (err) {
      setInstTasks(savedTasks);
      setInstTasksMsg({ severity: 'error', text: 'Bulk assign failed' });
    }
  };

  const handleInstBulkAssign = async (phaseNum, roomName) => {
    // Require at least one of: assignee, scheduled date
    if (!instBulkAssignee && !instBulkScheduledDate) return;

    const assigneeId = instBulkAssignee ? parseInt(instBulkAssignee) : null;
    const assigneeName = assigneeId
      ? instUsers.find((u) => u.id === assigneeId)?.name || ''
      : null;
    const scheduledDate = instBulkScheduledDate || null;

    // Optimistic update
    const savedTasks = instTasks;
    setInstTasks((prev) => {
      if (!prev) return prev;
      const updatedPhases = prev.phases.map((ph) => {
        if (ph.phase !== phaseNum) return ph;
        return {
          ...ph,
          tasks: ph.tasks.map((t) => {
            if (roomName && t.room_name !== roomName) return t;
            const next = { ...t };
            if (assigneeId) {
              next.assignee_id = assigneeId;
              next.assignee_name = assigneeName;
            }
            if (scheduledDate !== null || instBulkScheduledDate === '') {
              // Only overwrite if user actually picked a date
              if (instBulkScheduledDate) next.scheduled_date = scheduledDate;
            }
            return next;
          }),
        };
      });
      const allTasks = updatedPhases.flatMap((ph) => ph.tasks);
      const assigned = allTasks.filter((t) => t.assignee_id).length;
      return { ...prev, phases: updatedPhases, summary: { ...prev.summary, assigned } };
    });
    setInstBulkAssignPhase(null);
    setInstBulkAssignee('');
    setInstBulkScheduledDate('');

    try {
      const payload = { phase: phaseNum, room_name: roomName };
      if (assigneeId) payload.assignee_id = assigneeId;
      if (scheduledDate) payload.scheduled_date = scheduledDate;
      await bulkAssignTasks(id, payload);
      getInstallationTasks(id).then((reload) => setInstTasks(reload.data)).catch(() => {});
    } catch (err) {
      setInstTasks(savedTasks);
      setInstTasksMsg({ severity: 'error', text: 'Bulk assign failed' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return <Typography>Project not found</Typography>;
  }

  const completedTasks = project.tasks?.filter((t) => t.status === 'completed').length || 0;
  const totalTasks = project.tasks?.length || 0;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // isArchived / canEditProject / canEditStatus / isMgmt are declared higher
  // up (right after project state) so they're in scope for all useEffect
  // dependency arrays. See comment block there for full semantics.

  // PM, Sales, and Management can always advance; others need all tasks completed
  const canAdvance = !isArchived && project.current_phase < 7 && (canEditProject || completedTasks === totalTasks);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/projects')}
        sx={{ mb: 2 }}
      >
        Back to Projects
      </Button>

      {/* ARCHIVED banner — read-only mode for completed/closed projects.
          Tom Clancy 2026-05-20. Project is read-only when status='completed'
          or bucket is in ('archive','archive_dupe','completed').
          Management retains the ability to edit Status (un-archive). */}
      {isArchived && (
        <Alert
          severity="info"
          icon={<ArchiveIcon />}
          sx={{
            mb: 2,
            bgcolor: '#37474f',
            color: '#fff',
            '& .MuiAlert-icon': { color: '#cfd8dc' },
            border: '1px solid #263238',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ fontWeight: 700, letterSpacing: 1, fontSize: '0.95rem' }}>
              ARCHIVED — READ ONLY
            </Box>
            <Box sx={{ fontSize: '0.85rem', opacity: 0.95 }}>
              This project is closed (status: <em>{(project.status || '').replace('_', ' ') || 'completed'}</em>
              {project.bucket && project.bucket !== project.status ? `, bucket: ${project.bucket}` : ''}).
              Edits and phase advancement are disabled. Data is preserved for reference.
              {isMgmt ? ' To re-open, change Status above.' : ''}
            </Box>
          </Box>
        </Alert>
      )}

      {/* Timeline review mandate banner */}
      {isPmOrMgmt && timelineNeedsReview && !timelineBannerDismissed && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => {
                  setActiveTab(getTabIndex('timeline', canViewProposal));
                  setTimeout(() => sandboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                }}
              >
                Review Timeline Now
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setTimelineBannerDismissed(true)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          {timelineReason === 'overdue_review'
            ? `⚠️ Timeline was last reviewed ${timelineDaysSince} day${timelineDaysSince !== 1 ? 's' : ''} ago. Please confirm or update your project dates.`
            : timelineMissingPhases.length > 0
              ? `⚠️ Timeline dates missing for: ${timelineMissingPhases.join(', ')}. Please set estimated dates for each phase.`
              : '⚠️ This project\'s timeline has not been set. Please set estimated dates for each phase.'
          }
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            {project.primary_job_number && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ m: 0 }}>
                  Job #{project.primary_job_number}
                </Typography>
                {/* Pending Change Order badge — visible whenever count > 0.
                    Click scrolls to the full banner in the Project Sandbox.
                    Tom Clancy 2026-05-14. */}
                {pendingCOs.count > 0 && (
                  <Chip
                    size="small"
                    label={`${pendingCOs.count} pending CO${pendingCOs.count === 1 ? '' : 's'}${pendingCOs.aged_count > 0 ? ` • ${pendingCOs.aged_count} aged` : ''}`}
                    onClick={() => sandboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      bgcolor: pendingCOs.aged_count > 0 ? '#e65100' : '#1976d2',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: pendingCOs.aged_count > 0 ? '#bf360c' : '#0d47a1',
                      },
                    }}
                    title={pendingCOs.aged_count > 0
                      ? `Click to view — ${pendingCOs.aged_count} change order${pendingCOs.aged_count === 1 ? ' has' : 's have'} been pending ≥14 days`
                      : 'Click to view pending change orders'}
                  />
                )}
              </Box>
            )}
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {project.name}
            </Typography>
            {project.ar_number ? (
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
                onClick={() => navigate(`/clients/${encodeURIComponent(project.ar_number)}`)}
                sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
              >
                {project.client_name}
              </Typography>
            ) : (
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {project.client_name}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {project.site_address || project.client_address}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <VoiceNote projectId={id} onSaved={handleVoiceNoteSaved} userRole={user?.role} />
            <Chip
              label={project.health_status}
              color={
                project.health_status === 'green'
                  ? 'success'
                  : project.health_status === 'yellow'
                  ? 'warning'
                  : 'error'
              }
              sx={{ textTransform: 'capitalize' }}
            />
            {canEditProject && project.total_proposal_value > 0 && (
              <Chip
                icon={<AttachMoneyIcon />}
                label={fmtCurrency(project.total_proposal_value)}
                color="success"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="caption" color="text.secondary">
              Job Number
            </Typography>
            <Typography variant="body1" fontWeight="medium" color="primary.main">
              {project.primary_job_number || '—'}
            </Typography>
          </Grid>
          {canEditProject && (
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary">
                AR Number
              </Typography>
              {project.ar_number ? (
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color="primary.main"
                  onClick={() => navigate(`/clients/${encodeURIComponent(project.ar_number)}`)}
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  {project.ar_number} ↗
                </Typography>
              ) : (
                <Typography variant="body1" fontWeight="medium">—</Typography>
              )}
              {project.ar_number && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  onClick={() => navigate(`/clients/${encodeURIComponent(project.ar_number)}`)}
                  sx={{ cursor: 'pointer', display: 'block', '&:hover': { textDecoration: 'underline' } }}
                >
                  View all client projects →
                </Typography>
              )}
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="caption" color="text.secondary">
              Project Manager
            </Typography>
            {canEditProject && editingPM ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Autocomplete
                  size="small"
                  options={allUsers}
                  getOptionLabel={(u) => u.name || u.email || ''}
                  value={selectedPM}
                  onChange={(_, val) => setSelectedPM(val)}
                  renderInput={(params) => <TextField {...params} placeholder="Select PM" />}
                  sx={{ minWidth: 160 }}
                  isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                />
                <Tooltip title="Save">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={async () => {
                      if (!selectedPM) return;
                      try {
                        await assignProject(id, { pm_id: selectedPM.id });
                        setEditingPM(false);
                        setAssignSnackMsg(`Project Manager set to ${selectedPM.name}`);
                        setAssignSnackOpen(true);
                        loadProject();
                      } catch {
                        setAssignSnackMsg('Failed to update Project Manager');
                        setAssignSnackOpen(true);
                      }
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton size="small" onClick={() => { setEditingPM(false); setSelectedPM(null); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1" fontWeight="medium">
                  {project.pm_name || 'Unassigned'}
                </Typography>
                {canEditProject && (
                  <Tooltip title="Edit Project Manager">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const current = allUsers.find((u) => u.name === project.pm_name) || null;
                        setSelectedPM(current);
                        setEditingPM(true);
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="caption" color="text.secondary">
              Sales Representative
            </Typography>
            {canEditProject && editingSales ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Autocomplete
                  size="small"
                  options={allUsers}
                  getOptionLabel={(u) => u.name || u.email || ''}
                  value={selectedSales}
                  onChange={(_, val) => setSelectedSales(val)}
                  renderInput={(params) => <TextField {...params} placeholder="Select Sales Rep" />}
                  sx={{ minWidth: 160 }}
                  isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                />
                <Tooltip title="Save">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={async () => {
                      if (!selectedSales) return;
                      try {
                        await assignProject(id, { sales_id: selectedSales.id });
                        setEditingSales(false);
                        setAssignSnackMsg(`Sales Representative set to ${selectedSales.name}`);
                        setAssignSnackOpen(true);
                        loadProject();
                      } catch {
                        setAssignSnackMsg('Failed to update Sales Representative');
                        setAssignSnackOpen(true);
                      }
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton size="small" onClick={() => { setEditingSales(false); setSelectedSales(null); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1" fontWeight="medium">
                  {project.sales_name || 'Unassigned'}
                </Typography>
                {canEditProject && (
                  <Tooltip title="Edit Sales Representative">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const current = allUsers.find((u) => u.name === project.sales_name) || null;
                        setSelectedSales(current);
                        setEditingSales(true);
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Grid>
          {project.dtools_progress && (
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary">
                D-Tools Progress
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {project.dtools_progress}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="caption" color="text.secondary">
              D-Tools Reference
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {project.d_tools_ref || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            {canEditStatus && editingStatus ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="on_hold">On Hold</MenuItem>
                    <MenuItem value="review">Review</MenuItem>
                    <MenuItem value="final_billing">Final Billing</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Save">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={async () => {
                      try {
                        await updateProject(id, { status: selectedStatus });
                        setEditingStatus(false);
                        setAssignSnackMsg(`Status updated to ${selectedStatus.replace('_', ' ')}`);
                        setAssignSnackOpen(true);
                        loadProject();
                      } catch {
                        setAssignSnackMsg('Failed to update status');
                        setAssignSnackOpen(true);
                      }
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton size="small" onClick={() => setEditingStatus(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                  {(project.status || '').replace('_', ' ')}
                </Typography>
                {canEditStatus && (
                  <Tooltip title={isArchived ? 'Edit Status (un-archive)' : 'Edit Status'}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedStatus(project.status || 'active');
                        setEditingStatus(true);
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Labor Hours Section */}
        {!isSectionHidden('labor') && (project.labor_hours_sold > 0 || project.labor_hours_used > 0) && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Labor Hours
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Sold</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {parseFloat(project.labor_hours_sold || 0).toLocaleString(undefined, {maximumFractionDigits: 1})}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Used</Typography>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  {parseFloat(project.labor_hours_used || 0).toLocaleString(undefined, {maximumFractionDigits: 1})}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Remaining</Typography>
                <Typography variant="h6" fontWeight="bold" color={parseFloat(project.labor_hours_remaining || 0) < 0 ? 'error.main' : 'success.main'}>
                  {parseFloat(project.labor_hours_remaining || 0).toLocaleString(undefined, {maximumFractionDigits: 1})}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">% Used</Typography>
                <Typography variant="h6" fontWeight="bold" color={
                  project.labor_hours_sold > 0
                    ? (project.labor_hours_used / project.labor_hours_sold) > 0.9 ? 'error.main'
                    : (project.labor_hours_used / project.labor_hours_sold) > 0.75 ? 'warning.main'
                    : 'success.main'
                    : 'text.secondary'
                }>
                  {project.labor_hours_sold > 0
                    ? Math.round((project.labor_hours_used / project.labor_hours_sold) * 100) + '%'
                    : '—'}
                </Typography>
              </Grid>
            </Grid>
            {project.labor_hours_sold > 0 && (
              <LinearProgress
                variant="determinate"
                value={Math.min(100, project.labor_hours_sold > 0 ? (project.labor_hours_used / project.labor_hours_sold) * 100 : 0)}
                sx={{ mt: 1, height: 8, borderRadius: 4,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: (project.labor_hours_used / project.labor_hours_sold) > 1.0 ? 'error.main'
                      : (project.labor_hours_used / project.labor_hours_sold) > 0.9 ? 'error.main'
                      : (project.labor_hours_used / project.labor_hours_sold) > 0.75 ? 'warning.main'
                      : 'primary.main'
                  }
                }}
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Labor data from Sage Job Master. Updated daily.
            </Typography>
          </Box>
        )}

        {/* Labor Detail (collapsible) — only show when labor_hours_used > 0 */}
        {!isSectionHidden('labor') && project.labor_hours_used > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="text"
              onClick={handleLaborToggle}
              endIcon={laborExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ color: 'text.secondary', textTransform: 'none', pl: 0 }}
            >
              {laborExpanded ? 'Hide' : 'Show'} Labor Detail
            </Button>

            {laborExpanded && (
              <Box sx={{ mt: 1 }}>
                {laborLoading ? (
                  <CircularProgress size={24} />
                ) : laborEntries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No labor entries found for this project's job numbers.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          {[
                            { key: 'employee_name', label: 'Employee', align: 'left' },
                            { key: 'date_in',       label: 'Date',     align: 'left' },
                            { key: 'job_number',    label: 'Job #',    align: 'left' },
                            { key: 'task_code',     label: 'Task',     align: 'left' },
                            { key: 'hours',         label: 'Hours',    align: 'right' },
                            { key: 'notes',         label: 'Notes',    align: 'left' },
                          ].map(col => (
                            <TableCell key={col.key} align={col.align} sx={{ fontWeight: 'bold' }}>
                              <TableSortLabel
                                active={laborSortBy === col.key}
                                direction={laborSortBy === col.key ? laborSortDir : 'asc'}
                                onClick={() => handleLaborSort(col.key)}
                              >
                                {col.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortLaborEntries(laborEntries).map((entry) => (
                          <TableRow key={entry.id} hover>
                            <TableCell>
                              <Typography variant="body2">{entry.employee_name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {entry.date_in ? format(new Date(entry.date_in), 'MMM d') : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="primary.main" fontWeight="medium">
                                {entry.job_number || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{entry.task_code || '—'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                {parseFloat(entry.hours || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                                {entry.notes || ''}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>


      {!isSectionHidden('optiportal') && <OptiPortalReports project={project} />}

      {!isSectionHidden('dropbox') && (project.dropbox_link || project.dropbox_main || project.dropbox_av_eng_link || project.dropbox_av_eng || project.dropbox_av_rack_link || project.dropbox_av_racks || project.dropbox_acs_production) ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            📂 Project Documents
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {(project.dropbox_link || project.dropbox_main) && (
              <Button
                component="a"
                href={(project.dropbox_link || project.dropbox_main || '').replace(/&amp;/g, '&')}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<LaunchIcon />}
                sx={{ textTransform: 'none', bgcolor: '#0061FE', '&:hover': { bgcolor: '#0052d4' } }}
              >
                📁 Project Drawings
              </Button>
            )}
            {(project.dropbox_av_eng_link || project.dropbox_av_eng) && (
              <Button
                component="a"
                href={(project.dropbox_av_eng_link || project.dropbox_av_eng || '').replace(/&amp;/g, '&')}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<LaunchIcon />}
                sx={{ textTransform: 'none', bgcolor: '#00695c', '&:hover': { bgcolor: '#004d40' } }}
              >
                📐 AV Engineering
              </Button>
            )}
            {(project.dropbox_av_rack_link || project.dropbox_av_racks) && (
              <Button
                component="a"
                href={(project.dropbox_av_rack_link || project.dropbox_av_racks || '').replace(/&amp;/g, '&')}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<LaunchIcon />}
                sx={{ textTransform: 'none', bgcolor: '#4e342e', '&:hover': { bgcolor: '#3e2723' } }}
              >
                🔧 AV Racks
              </Button>
            )}
            {project.dropbox_acs_production && (
              <Button
                component="a"
                href={(project.dropbox_acs_production || '').replace(/&amp;/g, '&')}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<LaunchIcon />}
                sx={{ textTransform: 'none', bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}
              >
                🏭 ACS Production
              </Button>
            )}
          </Box>
        </Paper>
      ) : null}

      {/* Also Known As: aliases (address, LLC, AR code, nickname) + sibling projects */}
      <AlsoKnownAsPanel projectId={project.id} />

      <Paper elevation={3} sx={{ p: 3, borderTop: '5px solid #0D2C54', borderRadius: '8px' }}>
        <Box ref={sandboxRef} sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#0D2C54', letterSpacing: '0.5px', lineHeight: 1.2 }}>
            📦 Project Sandbox
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontStyle: 'italic' }}>
            Your single source for everything project-related — documents, timelines, tasks, communication, and financials all in one place.
          </Typography>

          {/* Pending Change Orders banner. Visible when there are unapproved
              revised D-Tools proposals for this project. PMs see what's still
              waiting on client / sales sign-off. Tom Clancy 2026-05-14. */}
          {pendingCOs.count > 0 && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 1,
                bgcolor: pendingCOs.aged_count > 0 ? '#fff4e5' : '#e8f4fd',
                border: '1px solid',
                borderColor: pendingCOs.aged_count > 0 ? '#ffa726' : '#90caf9',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 18, lineHeight: 1.2 }}>
                {pendingCOs.aged_count > 0 ? '⚠️' : '📝'}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: pendingCOs.aged_count > 0 ? '#e65100' : '#0d47a1' }}>
                  {pendingCOs.count} pending change order{pendingCOs.count === 1 ? '' : 's'} in D-Tools
                  {pendingCOs.aged_count > 0 && (
                    <Typography component="span" variant="body2" sx={{ ml: 1, fontWeight: 600, color: '#e65100' }}>
                      — {pendingCOs.aged_count} aged ≥14 days
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {pendingCOs.pending.map((co) => (
                    <Box
                      key={co.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        fontSize: '0.825rem',
                      }}
                    >
                      <Chip
                        size="small"
                        label={`Rev ${co.revision || '?'}`}
                        sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#fff', border: '1px solid #ccc' }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {co.name || co.dtools_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        • {co.sales_rep || 'no sales rep'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        • published {co.published_on ? fmtDate(co.published_on) : '—'}
                        {co.age_days != null && co.age_days >= 0 && ` (${co.age_days}d ago)`}
                      </Typography>
                      {co.is_aged && (
                        <Chip
                          size="small"
                          label="AGED"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#e65100', color: '#fff' }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', fontStyle: 'italic' }}>
                  Source: D-Tools. "Pending" = unapproved proposal revision ≥ 2. Last synced {pendingCOs.pending[0]?.synced_at ? fmtDate(pendingCOs.pending[0].synced_at) : '—'}.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 44 },
            '& .Mui-selected': { color: '#0D2C54 !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#0D2C54', height: 3 },
          }}
        >
          {canViewProposal && <Tab label="Proposal" />}
          <Tab label="Contacts" />
          <Tab label="Activity" />
          <Tab label="Field Notes" />
          <Tab label="Assignments" />
          <Tab label="Installation Tasks" icon={<BuildIcon />} iconPosition="start" />
          <Tab label="Timeline" icon={<CalendarTodayIcon />} iconPosition="start" />
          <Tab label="Rack Builder" icon={<StorageIcon />} iconPosition="start" />
          <Tab label="Bin" icon={<InventoryIcon />} iconPosition="start" />
          <Tab label="Purchase Orders" icon={<ShoppingCartIcon />} iconPosition="start" />
          <Tab label="Unbillable Labor" icon={<MoneyOffIcon />} iconPosition="start" />
          <Tab
            label={
              <Badge badgeContent={messagesUnread || 0} color="error" max={99}>
                The Wire
              </Badge>
            }
            icon={<ChatBubbleIcon />}
            iconPosition="start"
          />
          {canEditProject && hasProfitability && <Tab label="Financial" />}
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {/* Tab content — indices computed dynamically based on isMgmt */}
          {activeTab === getTabIndex('activity', canViewProposal) && (
            <Box>
              {/* Meeting Notes — shown above activity list */}
              <MeetingNotesSection projectId={project.id} />

              {/* Note filter */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FilterListIcon fontSize="small" color="action" />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Filter Notes</InputLabel>
                  <Select
                    value={noteFilter}
                    label="Filter Notes"
                    onChange={(e) => setNoteFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Notes</MenuItem>
                    <MenuItem value="PM Notes">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2E7D32' }} />
                        PM Notes
                      </Box>
                    </MenuItem>
                    <MenuItem value="Tech Notes">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1565C0' }} />
                        Tech Notes
                      </Box>
                    </MenuItem>
                    <MenuItem value="AfterCare Notes">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FF8F00' }} />
                        AfterCare Notes
                      </Box>
                    </MenuItem>
                    <MenuItem value="Lighting & Shades">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#7B1FA2' }} />
                        Lighting & Shades
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <List disablePadding>
                {(() => {
                  const filtered = (project.activity || []).filter(a => {
                    if (noteFilter === 'all') return true;
                    // Match by explicit note type action first, then fall back to user role
                    if (a.action === 'pm_note' && noteFilter === 'PM Notes') return true;
                    if (a.action === 'tech_note' && noteFilter === 'Tech Notes') return true;
                    if (a.action === 'aftercare_note' && noteFilter === 'AfterCare Notes') return true;
                    // Fall back to role-based matching for non-typed notes
                    if (['pm_note','tech_note','aftercare_note'].includes(a.action)) return false;
                    return getNoteCategory(a.user_role) === noteFilter;
                  });
                  if (filtered.length === 0) return (
                    <Typography color="text.secondary">
                      {noteFilter === 'all' ? 'No activity yet' : `No ${noteFilter.toLowerCase()} found`}
                    </Typography>
                  );
                  return filtered.map((activity) => {
                    const roleColor = getNoteColor(activity.user_role);
                    const isVoice = activity.action === 'voice_note' || activity.action === 'pm_note' || activity.action === 'tech_note' || activity.action === 'aftercare_note';
                    return (
                      <React.Fragment key={activity.id}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{ py: 1.5, borderLeft: `3px solid ${roleColor}`, pl: 2 }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                {isVoice && <MicIcon fontSize="small" sx={{ color: roleColor }} />}
                                <Box
                                  sx={{
                                    width: 8, height: 8, borderRadius: '50%',
                                    bgcolor: roleColor, flexShrink: 0,
                                    ...( isVoice ? { display: 'none' } : {})
                                  }}
                                />
                                <Typography variant="body2" fontWeight={700} sx={{ color: roleColor }}>
                                  {activity.action === 'pm_note' ? 'PM Note' :
                                   activity.action === 'tech_note' ? 'Tech Note' :
                                   activity.action === 'aftercare_note' ? 'AfterCare Note' :
                                   activity.action === 'voice_note' ? 'Voice Note' :
                                   activity.action}
                                </Typography>
                                <Chip
                                  label={activity.user_role || 'System'}
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: '0.65rem',
                                    bgcolor: `${roleColor}15`, color: roleColor,
                                    border: `1px solid ${roleColor}30`,
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {activity.user_name} • {(() => {
                                    if (!activity.created_at) return '—';
                                    const dt = new Date(activity.created_at);
                                    return isNaN(dt.getTime()) ? '—' : format(dt, 'MMM d, yyyy h:mm a');
                                  })()}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              activity.details ? (
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary', pl: 0.5, mt: 0.5 }}>
                                  {activity.details}
                                </Typography>
                              ) : null
                            }
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    );
                  });
                })()}
              </List>
            </Box>
          )}

          {activeTab === getTabIndex('contacts', canViewProposal) && (
            <ProjectContacts projectId={project.id} />
          )}


          {activeTab === getTabIndex('field_notes', canViewProposal) && (
            <FieldNotes projectId={project.id} />
          )}

          {activeTab === getTabIndex('assignments', canViewProposal) && (
            <WorkItems projectId={project.id} />
          )}

          {activeTab === getTabIndex('installation', canViewProposal) && (
            <InstallationTasksTab
              project={project}
              instTasks={instTasks}
              instTasksLoading={instTasksLoading}
              instTasksGenerating={instTasksGenerating}
              instTasksMsg={instTasksMsg}
              setInstTasksMsg={setInstTasksMsg}
              instExpandedPhases={instExpandedPhases}
              setInstExpandedPhases={setInstExpandedPhases}
              instExpandedRooms={instExpandedRooms}
              setInstExpandedRooms={setInstExpandedRooms}
              instExpandedTask={instExpandedTask}
              setInstExpandedTask={setInstExpandedTask}
              instUsers={instUsers}
              instBulkAssignPhase={instBulkAssignPhase}
              setInstBulkAssignPhase={setInstBulkAssignPhase}
              instBulkAssignee={instBulkAssignee}
              setInstBulkAssignee={setInstBulkAssignee}
              instBulkScheduledDate={instBulkScheduledDate}
              setInstBulkScheduledDate={setInstBulkScheduledDate}
              instPhaseGateDialog={instPhaseGateDialog}
              setInstPhaseGateDialog={setInstPhaseGateDialog}
              instCompletionDate={instCompletionDate}
              setInstCompletionDate={setInstCompletionDate}
              onGenerate={handleGenerateInstallationTasks}
              onTaskUpdate={handleInstTaskUpdate}
              onBulkAssign={handleInstBulkAssign}
              isPmOrMgmt={isPmOrMgmt}
              onSaveCompletionDate={handleSaveCompletionDate}
              onSwitchToInstallation={() => setActiveTab(getTabIndex('installation', canViewProposal))}
              instViewMode={instViewMode}
              setInstViewMode={setInstViewMode}
              instSelectedTaskIds={instSelectedTaskIds}
              setInstSelectedTaskIds={setInstSelectedTaskIds}
              instFilterDate={instFilterDate}
              setInstFilterDate={setInstFilterDate}
              instFilterAssignment={instFilterAssignment}
              setInstFilterAssignment={setInstFilterAssignment}
              instFilterAssignee={instFilterAssignee}
              setInstFilterAssignee={setInstFilterAssignee}
              instPlannerAssignee={instPlannerAssignee}
              setInstPlannerAssignee={setInstPlannerAssignee}
              instPlannerScheduledDate={instPlannerScheduledDate}
              setInstPlannerScheduledDate={setInstPlannerScheduledDate}
              onPlannerApply={handlePlannerApply}
            />
          )}

          {activeTab === getTabIndex('timeline', canViewProposal) && (
            <TimelineTab
              projectId={project.id}
              onSaved={() => {
                setTimelineNeedsReview(false);
                setTimelineBannerDismissed(false);
                checkTimelineStatus();
              }}
            />
          )}

          {activeTab === getTabIndex('rack_builder', canViewProposal) && (
            <RackBuilder projectId={project.id} />
          )}

          {activeTab === getTabIndex('bin', canViewProposal) && (
            <BinTab projectId={project.id} project={project} />
          )}

          {activeTab === getTabIndex('purchase_orders', canViewProposal) && (
            <PurchaseOrdersTab projectId={project.id} />
          )}

          {activeTab === getTabIndex('unbillable_labor', canViewProposal) && (
            <UnbillableLaborTab projectId={project.id} />
          )}

          {activeTab === getTabIndex('messages', canViewProposal) && (
            <ProjectMessagesTab projectId={project.id} />
          )}

          {canEditProject && hasProfitability && activeTab === getTabIndex('financial', canViewProposal) && (
            <List>
              {project.financial_refs?.length === 0 ? (
                <Typography color="text.secondary">No financial records</Typography>
              ) : (
                project.financial_refs?.map((ref) => (
                  <React.Fragment key={ref.id}>
                    <ListItem>
                      <ListItemText
                        primary={`${ref.ref_type}: ${ref.ref_number}`}
                        secondary={`Amount: $${ref.amount?.toLocaleString() || 0} • Status: ${ref.status}`}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </List>
          )}

          {canViewProposal && activeTab === getTabIndex('proposal', canViewProposal) && (
            <Box>
              {/* ── Client Documents ─────────────────────────────────── */}
              <ClientDocuments projectId={project.id} />

              {/* ── Generate Installation Tasks button ─────────────── */}
              {(!instTasks || instTasks.summary?.total_tasks === 0) && (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      disabled={instTasksGenerating}
                      startIcon={instTasksGenerating ? <CircularProgress size={14} color="inherit" /> : <BuildIcon />}
                      onClick={async () => {
                        await handleGenerateInstallationTasks();
                        setActiveTab(getTabIndex('installation', canViewProposal));
                      }}
                    >
                      {instTasksGenerating ? 'Generating…' : 'Generate Tasks'}
                    </Button>
                  }
                >
                  Review the proposal below, then generate installation tasks from the sold items.
                </Alert>
              )}
              {(instTasks && instTasks.summary?.total_tasks > 0) && (
                <Alert severity="success" sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" variant="outlined" onClick={() => setActiveTab(getTabIndex('installation', canViewProposal))}>
                      View Tasks
                    </Button>
                  }
                >
                  {instTasks.summary.total_tasks} installation tasks generated — {instTasks.summary.completed} completed, {instTasks.summary.assigned} assigned.
                </Alert>
              )}

              {/* ── CRM Deals section (existing) ───────────────────── */}
              {project.deals?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>CRM Deals</Typography>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total Proposal Value</Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        {fmtCurrency(project.total_proposal_value)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Proposals</Typography>
                      <Typography variant="h6" fontWeight="bold">{project.deals.length}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Approved</Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        {project.deals.filter((d) => d.approved).length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Closed Won</Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.dark">
                        {project.deals.filter((d) => d.crm_stage === 'Closed Won').length}
                      </Typography>
                    </Box>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          {[
                            { key: 'job_number',             label: 'Job #' },
                            { key: 'project_name',           label: 'Deal / System' },
                            { key: 'crm_stage',              label: 'Stage' },
                            { key: 'proposal_price',         label: 'Proposal Price' },
                            { key: 'approved',               label: 'Approved' },
                            { key: 'business_unit',          label: 'Business Unit' },
                            { key: 'installation_start_date',label: 'Install Start' },
                            { key: 'expected_move_in_date',  label: 'Move-In' },
                            { key: 'ar_number',              label: 'AR #' },
                          ].map(col => (
                            <TableCell key={col.key} sx={{ fontWeight: 'bold' }}>
                              <TableSortLabel
                                active={dealsSortBy === col.key}
                                direction={dealsSortBy === col.key ? dealsSortDir : 'asc'}
                                onClick={() => handleDealsSort(col.key)}
                              >
                                {col.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortDeals(project.deals).map((deal) => (
                          <TableRow key={deal.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" color="primary.main">
                                {deal.job_number || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {deal.project_name || '—'}
                              </Typography>
                              {deal.system_type && (
                                <Chip label={deal.system_type} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={deal.crm_stage || '—'}
                                size="small"
                                color={
                                  deal.crm_stage === 'Closed Won'
                                    ? 'success'
                                    : deal.crm_stage === 'Closed Lost'
                                    ? 'error'
                                    : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={deal.proposal_price ? 'bold' : 'normal'}
                                color={deal.proposal_price ? 'success.main' : 'text.secondary'}
                              >
                                {fmtCurrency(deal.proposal_price)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={deal.approved ? 'Yes' : 'No'}
                                size="small"
                                color={deal.approved ? 'success' : 'default'}
                                variant={deal.approved ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{deal.business_unit || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{fmtDate(deal.installation_start_date)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{fmtDate(deal.expected_move_in_date)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">{deal.ar_number || '—'}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {!project.deals?.length && (
                <Alert severity="info" sx={{ mb: 3 }}>No CRM deals linked to this project yet. Run a CRM sync to import deals.</Alert>
              )}

              {/* ── D-Tools Proposal section ───────────────────────── */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">D-Tools Proposal</Typography>
                  {proposal && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {proposalRegen?.last_regeneration && (
                        <Typography variant="caption" color="text.secondary">
                          Last regenerated {fmtDate(proposalRegen.last_regeneration.regenerated_at)} by {proposalRegen.last_regeneration.regenerated_by_name || '—'}
                        </Typography>
                      )}
                      <Button size="small" variant="text" onClick={openRegenHistory}>
                        History
                      </Button>
                      {canRegenerate && (
                        <Button
                          size="small"
                          variant={proposalRegen?.stale ? 'contained' : 'outlined'}
                          color={proposalRegen?.stale ? 'warning' : 'primary'}
                          onClick={() => { setRegenError(null); setRegenDialogOpen(true); }}
                        >
                          Regenerate Proposal Artifacts
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Stale banner: approved COs not yet incorporated */}
                {proposal && proposalRegen?.stale && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={canRegenerate ? (
                      <Button color="inherit" size="small" onClick={() => { setRegenError(null); setRegenDialogOpen(true); }}>
                        Regenerate now
                      </Button>
                    ) : null}
                  >
                    <strong>Stale</strong> — {proposalRegen.pending_co_count} approved change order{proposalRegen.pending_co_count === 1 ? '' : 's'} not yet incorporated into the proposal notes / labor report.
                    {proposalRegen.pending_change_orders?.length > 0 && (
                      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                        {proposalRegen.pending_change_orders.slice(0, 5).map(co => (
                          <li key={co.id}>
                            <Typography variant="caption">
                              CO #{co.id} — {co.description?.substring(0, 80) || '(no description)'}
                              {co.cost_impact != null && ` · ${fmtCurrency(co.cost_impact)}`}
                              {co.approved_at && ` · approved ${fmtDate(co.approved_at)}`}
                            </Typography>
                          </li>
                        ))}
                        {proposalRegen.pending_change_orders.length > 5 && (
                          <li><Typography variant="caption">… and {proposalRegen.pending_change_orders.length - 5} more</Typography></li>
                        )}
                      </Box>
                    )}
                  </Alert>
                )}

                {regenSuccessMsg && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRegenSuccessMsg(null)}>
                    {regenSuccessMsg}
                  </Alert>
                )}

                {proposalLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
                    <CircularProgress size={20} />
                    <Typography color="text.secondary">Loading proposal data...</Typography>
                  </Box>
                )}

                {proposalError && !proposalLoading && (
                  <Alert severity="error">{proposalError}</Alert>
                )}

                {proposalLoaded && !proposal && !proposalLoading && (
                  <Alert severity="info" action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      disabled={proposalSyncing}
                      onClick={async () => {
                        setProposalSyncing(true);
                        try {
                          const token = localStorage.getItem('token');
                          await axios.post(`/api/projects/${id}/sync-proposal`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          setProposalSyncMsg('Sync complete! Refreshing...');
                          // Reload proposal data
                          const res = await getProjectProposal(id);
                          setProposal(res.data.proposal || null);
                        } catch (e) {
                          setProposalSyncMsg(e.response?.data?.error || 'Sync failed. The project may not exist in D-Tools.');
                        } finally {
                          setProposalSyncing(false);
                          setTimeout(() => setProposalSyncMsg(null), 5000);
                        }
                      }}
                    >
                      {proposalSyncing ? 'Syncing...' : 'Sync from D-Tools'}
                    </Button>
                  }>
                    No D-Tools proposal synced for this project yet.
                    {proposalSyncMsg && <><br />{proposalSyncMsg}</>}
                  </Alert>
                )}

                {proposal && !proposalLoading && (
                  <Box>
                    {/* Proposal Header Card */}
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="caption" color="text.secondary">Proposal Name</Typography>
                        <Typography variant="body1" fontWeight="bold">{proposal.name || '—'}</Typography>
                        {proposal.dtools_number && (
                          <Typography variant="caption" color="text.secondary">Job # {proposal.dtools_number}</Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Revision</Typography>
                        <Typography variant="body1" fontWeight="medium">{proposal.revision || '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={proposal.approved ? 'Approved' : 'Not Approved'}
                            size="small"
                            color={proposal.approved ? 'success' : 'default'}
                            variant={proposal.approved ? 'filled' : 'outlined'}
                          />
                        </Box>
                        {proposal.approved_on && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {fmtDate(proposal.approved_on)}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Sales Rep</Typography>
                        <Typography variant="body2">{proposal.sales_rep || '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Project Manager</Typography>
                        <Typography variant="body2">{proposal.project_manager || '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Total Labor Hours</Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          {proposal.total_labor_hours != null ? `${parseFloat(proposal.total_labor_hours).toFixed(1)} hrs` : '—'}
                        </Typography>
                      </Box>
                      {proposal.published_on && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Published</Typography>
                          <Typography variant="body2">{fmtDate(proposal.published_on)}</Typography>
                        </Box>
                      )}
                      <Box sx={{ flex: '1 1 100%' }}>
                        <Typography variant="caption" color="text.secondary">Last Synced</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{fmtDate(proposal.synced_at)}</Typography>
                      </Box>
                    </Box>

                    {/* Change Order badge */}
                    {proposal.is_change_order && (
                      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip label="Change Order" color="warning" size="small" />
                        {proposal.co_name && <Typography variant="body2" fontWeight="medium">{proposal.co_name}</Typography>}
                        {proposal.co_status && <Chip label={proposal.co_status} size="small" variant="outlined" />}
                        {proposal.co_notes && <Typography variant="caption" color="text.secondary">{proposal.co_notes}</Typography>}
                      </Box>
                    )}

                    {/* Scope of Work */}
                    {proposal.scope_of_work && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Scope of Work</Typography>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                          {proposal.scope_of_work.length > 500 && !scopeExpanded ? (
                            <>
                              <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {proposal.scope_of_work.substring(0, 500)}...
                              </Typography>
                              <Button size="small" onClick={() => setScopeExpanded(true)} sx={{ mt: 1, textTransform: 'none' }}>
                                Read More
                              </Button>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {proposal.scope_of_work}
                              </Typography>
                              {proposal.scope_of_work.length > 500 && (
                                <Button size="small" onClick={() => setScopeExpanded(false)} sx={{ mt: 1, textTransform: 'none' }}>
                                  Show Less
                                </Button>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Notes */}
                    {proposal.notes && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Notes</Typography>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{proposal.notes}</Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Category Summary */}
                    {proposalCategorySummary.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Equipment Summary by Category</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {proposalCategorySummary.map((cat) => (
                            <Box
                              key={cat.category}
                              sx={{
                                px: 1.5, py: 0.75,
                                bgcolor: 'primary.50',
                                border: '1px solid',
                                borderColor: 'primary.200',
                                borderRadius: 1,
                                minWidth: 120,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" display="block">{cat.category}</Typography>
                              <Typography variant="body2" fontWeight="bold">{cat.item_count} items</Typography>
                              {cat.labor_hours > 0 && (
                                <Typography variant="caption" color="primary.main">{cat.labor_hours.toFixed(1)} hrs</Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Room-by-Room Breakdown */}
                    {proposalItemsByLocation.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Room-by-Room Breakdown</Typography>
                        {proposalItemsByLocation.map((loc) => {
                          const isOpen = !!expandedRooms[loc.location_name];
                          return (
                            <Box
                              key={loc.location_name}
                              sx={{ mb: 1, border: '1px solid', borderColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}
                            >
                              {/* Room Header */}
                              <Box
                                onClick={() => toggleRoom(loc.location_name)}
                                sx={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  px: 2, py: 1.25,
                                  bgcolor: isOpen ? 'primary.main' : 'grey.100',
                                  color: isOpen ? 'primary.contrastText' : 'text.primary',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: isOpen ? 'primary.dark' : 'grey.200' },
                                  transition: 'background-color 0.15s',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                  <Typography variant="body2" fontWeight="bold">{loc.location_name}</Typography>
                                  <Chip
                                    label={`${loc.item_count} item${loc.item_count !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      height: 20, fontSize: '0.7rem',
                                      bgcolor: isOpen ? 'rgba(255,255,255,0.2)' : 'grey.300',
                                      color: isOpen ? 'primary.contrastText' : 'text.secondary',
                                    }}
                                  />
                                </Box>
                                {loc.labor_hours > 0 && (
                                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                    {loc.labor_hours.toFixed(1)} hrs labor
                                  </Typography>
                                )}
                              </Box>

                              {/* Room Content */}
                              {isOpen && (
                                <Box sx={{ px: 2, py: 1.5 }}>
                                  {(loc.by_category || []).map((catGroup) => (
                                    <Box key={catGroup.category} sx={{ mb: 2 }}>
                                      {/* Category subheader */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, pb: 0.5, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                        <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                          {catGroup.category}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {catGroup.item_count} item{catGroup.item_count !== 1 ? 's' : ''}
                                          {catGroup.labor_hours > 0 ? ` · ${catGroup.labor_hours.toFixed(1)} hrs` : ''}
                                        </Typography>
                                      </Box>

                                      {/* Items in this category */}
                                      {catGroup.items.map((item) => (
                                        <Box key={item.id}>
                                          {/* Main item row */}
                                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.4, pl: 1 }}>
                                            <Typography variant="body2" sx={{ minWidth: 28, color: 'text.secondary', flexShrink: 0 }}>
                                              {item.quantity != null ? `${parseFloat(item.quantity)}×` : '1×'}
                                            </Typography>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Typography variant="body2" fontWeight="medium" component="span">
                                                {[item.manufacturer, item.model].filter(Boolean).join(' ')}
                                              </Typography>
                                              {item.description && (
                                                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 0.75 }}>
                                                  — {item.description}
                                                </Typography>
                                              )}
                                            </Box>
                                            {(item.total_labor_hours > 0 || item.labor_hours > 0) && (
                                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                                                {parseFloat(item.total_labor_hours || item.labor_hours || 0).toFixed(2)} hrs
                                              </Typography>
                                            )}
                                          </Box>

                                          {/* Accessories indented */}
                                          {item.accessories && item.accessories.length > 0 && (
                                            <Box sx={{ pl: 5 }}>
                                              {item.accessories.map((acc) => (
                                                <Box key={acc.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.3 }}>
                                                  <Typography variant="caption" sx={{ minWidth: 28, color: 'text.disabled', flexShrink: 0 }}>
                                                    {acc.quantity != null ? `${parseFloat(acc.quantity)}×` : '1×'}
                                                  </Typography>
                                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="caption" color="text.secondary" component="span">
                                                      {[acc.manufacturer, acc.model].filter(Boolean).join(' ')}
                                                    </Typography>
                                                    {acc.description && (
                                                      <Typography variant="caption" color="text.disabled" component="span" sx={{ ml: 0.5 }}>
                                                        — {acc.description}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                  {(acc.total_labor_hours > 0 || acc.labor_hours > 0) && (
                                                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                                                      {parseFloat(acc.total_labor_hours || acc.labor_hours || 0).toFixed(2)} hrs
                                                    </Typography>
                                                  )}
                                                </Box>
                                              ))}
                                            </Box>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  ))}

                                  {/* Room totals footer */}
                                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'grey.100', display: 'flex', gap: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Total: <strong>{loc.item_count}</strong> items
                                    </Typography>
                                    {loc.labor_hours > 0 && (
                                      <Typography variant="caption" color="text.secondary">
                                        Labor: <strong>{loc.labor_hours.toFixed(1)} hrs</strong>
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Final Billing & Warranty Tracking */}
      {(project.current_phase >= 6 || project.final_bill_status || project.warranty_start_date) && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderLeft: '4px solid #c62828' }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>💰 Final Billing & Warranty</Typography>
          <Grid container spacing={3}>
            {/* Final Billing */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" color="error.main" gutterBottom>Final Billing</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  {canEditProject ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={project.final_bill_status || ''}
                        displayEmpty
                        onChange={async (e) => {
                          try {
                            await updateProject(id, { final_bill_status: e.target.value || null });
                            loadProject();
                          } catch {}
                        }}
                      >
                        <MenuItem value=""><em>Not Started</em></MenuItem>
                        <MenuItem value="started">Started</MenuItem>
                        <MenuItem value="released">Released</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{project.final_bill_status || 'Not Started'}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Started</Typography>
                  <TextField type="date" fullWidth size="small" value={project.final_bill_started_date || ''}
                    disabled={!canEditProject}
                    slotProps={{ inputLabel: { shrink: true } }}
                    onChange={async (e) => { try { await updateProject(id, { final_bill_started_date: e.target.value || null }); loadProject(); } catch {} }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Released</Typography>
                  <TextField type="date" fullWidth size="small" value={project.final_bill_released_date || ''}
                    disabled={!canEditProject}
                    slotProps={{ inputLabel: { shrink: true } }}
                    onChange={async (e) => { try { await updateProject(id, { final_bill_released_date: e.target.value || null }); loadProject(); } catch {} }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Paid</Typography>
                  <TextField type="date" fullWidth size="small" value={project.final_bill_paid_date || ''}
                    disabled={!canEditProject}
                    slotProps={{ inputLabel: { shrink: true } }}
                    onChange={async (e) => { try { await updateProject(id, { final_bill_paid_date: e.target.value || null }); loadProject(); } catch {} }}
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* Warranty */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>Warranty</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <TextField type="date" fullWidth size="small" value={project.warranty_start_date || ''}
                    disabled={!canEditProject}
                    slotProps={{ inputLabel: { shrink: true } }}
                    onChange={async (e) => { try { await updateProject(id, { warranty_start_date: e.target.value || null }); loadProject(); } catch {} }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  {canEditProject ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={project.warranty_type || ''}
                        displayEmpty
                        onChange={async (e) => { try { await updateProject(id, { warranty_type: e.target.value || null }); loadProject(); } catch {} }}
                      >
                        <MenuItem value=""><em>Select...</em></MenuItem>
                        <MenuItem value="1-year">1-Year Standard</MenuItem>
                        <MenuItem value="2-year">2-Year Extended</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{project.warranty_type || '—'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  {project.warranty_start_date && (() => {
                    const months = project.warranty_months || 12;
                    const start = new Date(project.warranty_start_date);
                    const expires = new Date(start);
                    expires.setMonth(expires.getMonth() + months);
                    const now = new Date();
                    const daysLeft = Math.ceil((expires - now) / 86400000);
                    const color = daysLeft < 0 ? 'error' : daysLeft < 30 ? 'warning' : daysLeft < 90 ? 'info' : 'success';
                    return (
                      <Alert severity={color} sx={{ mt: 1 }}>
                        <strong>Warranty {daysLeft < 0 ? 'expired' : 'expires'}:</strong>{' '}
                        {expires.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {daysLeft >= 0 ? ` (${daysLeft} days remaining)` : ` (${Math.abs(daysLeft)} days ago)`}
                        {daysLeft <= 30 && daysLeft >= 0 && ' — Time to discuss AfterCare plan!'}
                        {daysLeft < 0 && ' — Upsell opportunity: VIP or Basic plan'}
                      </Alert>
                    );
                  })()}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      {canEditProject && hasProfitability && !isSectionHidden('sage') && <SageFinancials projectId={id} />}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Phase Progress
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {PHASE_NAMES.map((name, index) => {
            const phaseNum = index + 1;
            const isCurrent = phaseNum === project.current_phase;
            const isCompleted = phaseNum < project.current_phase;

            return (
              <React.Fragment key={phaseNum}>
                <Box
                  onClick={async () => {
                    if (phaseNum === project.current_phase) return;
                    try {
                      await setProjectPhase(id, phaseNum);
                      setProject(prev => prev ? { ...prev, current_phase: phaseNum } : prev);
                    } catch (err) {
                      alert(err.response?.data?.error || 'Failed to change phase');
                    }
                  }}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    p: 1.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    bgcolor: isCurrent ? 'primary.main' : isCompleted ? 'success.light' : 'grey.200',
                    color: isCurrent || isCompleted ? 'white' : 'text.secondary',
                    border: isCurrent ? '2px solid' : '2px solid transparent',
                    borderColor: isCurrent ? 'primary.dark' : 'transparent',
                    '&:hover': {
                      bgcolor: isCurrent ? 'primary.main' : isCompleted ? 'success.main' : 'grey.300',
                      transform: 'scale(1.03)',
                    },
                  }}
                >
                  <Typography variant="caption" display="block" fontWeight="bold">
                    Phase {phaseNum}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {name}
                  </Typography>
                </Box>
                {index < PHASE_NAMES.length - 1 && (
                  <KeyboardArrowRightIcon sx={{ color: 'grey.400', mx: 0.5 }} />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title="Generate RACI template tasks for the current phase">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleGenerateRaciTasks}
                  disabled={generatingRaci}
                  sx={{ borderColor: '#7B1FA2', color: '#7B1FA2', '&:hover': { bgcolor: '#7B1FA210', borderColor: '#7B1FA2' } }}
                >
                  {generatingRaci ? 'Generating…' : '+ RACI Tasks'}
                </Button>
              </span>
            </Tooltip>
            {canAdvance && (
              <Button
                variant="contained"
                color="success"
                onClick={() => setAdvanceDialogOpen(true)}
              >
                Advance to Next Phase
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              {completedTasks} of {totalTasks} tasks completed
            </Typography>
            <Typography variant="body2">{Math.round(progress)}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
        </Box>

        {raciGenMsg && (
          <Alert
            severity={raciGenMsg.startsWith('Generated') ? 'success' : raciGenMsg.startsWith('All RACI') ? 'info' : 'error'}
            onClose={() => setRaciGenMsg(null)}
            sx={{ mb: 2 }}
          >
            {raciGenMsg}
          </Alert>
        )}

        {totalTasks === 0 ? (
          <Alert severity="info">
            No tasks for this phase yet.{' '}
            <Button
              size="small"
              onClick={handleGenerateRaciTasks}
              disabled={generatingRaci}
              sx={{ ml: 1, color: '#7B1FA2' }}
            >
              {generatingRaci ? 'Generating…' : 'Generate RACI Tasks'}
            </Button>
          </Alert>
        ) : (
          (() => {
            // Group tasks: RACI templates get their own group, Zoho by tasklist, others as Manual
            const groups = {};
            const groupOrder = [];

            for (const task of (project.tasks || [])) {
              let groupKey;
              if (task.source === 'raci_template') {
                groupKey = '📋 RACI Template Tasks';
              } else if (task.source === 'zoho' && task.zoho_tasklist) {
                groupKey = task.zoho_tasklist;
              } else {
                groupKey = 'Manual Tasks';
              }
              if (!groups[groupKey]) {
                groups[groupKey] = [];
                groupOrder.push(groupKey);
              }
              groups[groupKey].push(task);
            }

            const hasMultipleGroups = groupOrder.length > 1;

            const getGroupBorderColor = (name) => {
              if (name === '📋 RACI Template Tasks') return '#7B1FA2';
              if (name === 'Manual Tasks') return 'grey.400';
              return 'primary.light';
            };

            const getGroupBgColor = (name) => {
              if (name === '📋 RACI Template Tasks') return '#F3E5F5';
              return 'grey.100';
            };

            const getGroupTextColor = (name) => {
              if (name === '📋 RACI Template Tasks') return '#7B1FA2';
              return 'text.secondary';
            };

            return groupOrder.map((groupName) => (
              <Box key={groupName}>
                {hasMultipleGroups && (
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      bgcolor: getGroupBgColor(groupName),
                      borderLeft: '3px solid',
                      borderColor: getGroupBorderColor(groupName),
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: getGroupTextColor(groupName) }}
                    >
                      {groupName}
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, fontWeight: 'normal' }}>
                        ({groups[groupName].length} tasks)
                      </Typography>
                    </Typography>
                  </Box>
                )}
                <List disablePadding>
                  <TransitionGroup>
                    {groups[groupName].map((task) => (
                      <Collapse key={task.id}>
                        <TaskItem
                          task={task}
                          onStatusChange={handleTaskStatusChange}
                          onApprove={handleTaskApprove}
                          currentUser={user}
                          projectPmId={project.pm_id}
                          isPmOrMgmt={isPmOrMgmt}
                          onAssign={handleRaciTaskAssign}
                          allUsers={allUsers}
                        />
                      </Collapse>
                    ))}
                  </TransitionGroup>
                </List>
              </Box>
            ));
          })()
        )}
      </Paper>



      <Dialog open={advanceDialogOpen} onClose={() => setAdvanceDialogOpen(false)}>
        <DialogTitle>Advance to Next Phase?</DialogTitle>
        <DialogContent>
          <Typography>
            All tasks in the current phase are completed. Do you want to advance to{' '}
            {PHASE_NAMES[project.current_phase]}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvanceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAdvancePhase} variant="contained" color="success">
            Advance Phase
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={assignSnackOpen}
        autoHideDuration={4000}
        onClose={() => setAssignSnackOpen(false)}
        message={assignSnackMsg}
        action={
          <IconButton size="small" color="inherit" onClick={() => setAssignSnackOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      <Snackbar
        open={taskSnackOpen}
        autoHideDuration={3000}
        onClose={() => setTaskSnackOpen(false)}
        message={taskSnackMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <IconButton size="small" color="inherit" onClick={() => setTaskSnackOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      {/* Proposal Regeneration confirmation dialog */}
      <Dialog open={regenDialogOpen} onClose={() => !regenSubmitting && setRegenDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerate Proposal Artifacts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will re-sync the D-Tools proposal for this project and snapshot the resulting notes and labor report. Approved change orders not yet incorporated will be linked to this regeneration.
          </Typography>
          {proposalRegen?.pending_co_count > 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{proposalRegen.pending_co_count}</strong> approved change order{proposalRegen.pending_co_count === 1 ? '' : 's'} will be incorporated.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No new approved change orders detected. The regeneration will still re-sync from D-Tools and create a fresh snapshot.
            </Alert>
          )}
          <TextField
            label="Note (optional)"
            placeholder="e.g. Incorporating Hamptons CO-7 + CO-8 — added theater scope"
            value={regenNote}
            onChange={(e) => setRegenNote(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={regenSubmitting}
          />
          {regenError && <Alert severity="error" sx={{ mt: 2 }}>{regenError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenDialogOpen(false)} disabled={regenSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRegenerateProposal}
            disabled={regenSubmitting}
            startIcon={regenSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {regenSubmitting ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Proposal Regeneration history dialog */}
      <Dialog open={regenHistoryOpen} onClose={() => setRegenHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Proposal Regeneration History</DialogTitle>
        <DialogContent>
          {regenHistory === null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={20} /> <Typography variant="body2">Loading…</Typography>
            </Box>
          )}
          {regenHistory !== null && regenHistory.length === 0 && (
            <Typography variant="body2" color="text.secondary">No regenerations yet for this project.</Typography>
          )}
          {regenHistory !== null && regenHistory.length > 0 && (
            <Box>
              {regenHistory.map((r) => (
                <Box key={r.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        v{r.id} · {fmtDate(r.regenerated_at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {r.regenerated_by_name || '—'} · {r.trigger_co_count} CO{r.trigger_co_count === 1 ? '' : 's'} incorporated
                        {r.dtools_revision ? ` · D-Tools rev ${r.dtools_revision}` : ''}
                        {r.total_labor_hours != null ? ` · ${parseFloat(r.total_labor_hours).toFixed(1)} labor hrs` : ''}
                        {r.total_item_count != null ? ` · ${r.total_item_count} items` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={r.sync_status}
                      color={r.sync_status === 'success' ? 'success' : r.sync_status === 'failed' ? 'error' : 'warning'}
                      variant="outlined"
                    />
                  </Box>
                  {r.pm_note && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                      “{r.pm_note}”
                    </Typography>
                  )}
                  {r.sync_message && r.sync_status !== 'success' && (
                    <Typography variant="caption" color="warning.main" display="block">{r.sync_message}</Typography>
                  )}
                  {r.trigger_change_orders?.length > 0 && (
                    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                      {r.trigger_change_orders.map(co => (
                        <li key={co.id}>
                          <Typography variant="caption">
                            CO #{co.id} — {co.description?.substring(0, 100) || '(no description)'}
                            {co.cost_impact != null && ` · ${fmtCurrency(co.cost_impact)}`}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetail;
